import { useState, useMemo, useRef, useEffect } from "react"
import moment from "moment"
import firebase from "firebase"

// Algolia (same instance the quoting/build-quote search uses)
import algoliasearch from "algoliasearch/lite"
import { InstantSearch, connectSearchBox, connectHits, connectStateResults, Index } from "react-instantsearch-dom"

// Icons
import { Search as SearchGlyph, Plus, X, Trash2, Pencil, Plane, Car, AlertTriangle, ArrowLeftRight, Send, FileText, Clock, Plug, Check, Eye, Copy, TrainFront, ChevronDown } from "lucide-react"

import { db, auth, funEU } from "../../../../utils/firebase"
import { ReadOnlyProvider } from "../../../../utils/providers/read-only"
import { ROOM_TYPES } from "../../../../utils/exports/room-types"
import { BOARD_TYPES } from "../../../../utils/exports/board-types"
import { FEE_TYPES } from "../../../../assets/data/exports"
import { printCurrencySymbol } from "../../../quoting/utils/functions"
import { calculatePackageCost } from "../../../quoting/utils/package-cost"
import PackageModal from "./package-modal"
import FlightTransferModal from "./flight-transfer-modal"
import EnquiryViewModal from "./enquiry-view-modal"
import QuotePreviewModal from "../../../public-quote/quote-preview"
import { buildItineraryNodes, ItineraryTimeline, PreviewImage, normaliseBuggies } from "./itinerary-timeline"

// UI components
import Button from "../../../../components/ui/button/button"
import Input from "../../../../components/ui/inputs/input"
import Select from "../../../../components/ui/select/select"
import Datepicker from "../../../../components/ui/datepicker/datepicker"
import Timepicker from "../../../../components/ui/datepicker/timepicker"
import Badge from "../../../../components/ui/badge/badge"
import Checkbox from "../../../../components/ui/checkbox/checkbox"
import Stepper from "../../../../components/ui/stepper/stepper"
import Toggle from "../../../../components/ui/toggle/toggle"
import Segmented from "../../../../components/ui/segmented/segmented"
import { CheckIcon } from "../../../../utils/svgs"
import Loading from "../../../../components/app/loading/loading"

import "./new-quote.scss"

// Live tee-time read for a course's active availability connector. Lives in the
// europe-west2 connectors codebase, so it's reached through the regional handle.
// Replays the connector's saved recipe for one date — see functions-connectors.
const fetchConnectorTeeTimes = funEU.httpsCallable("fetchConnectorTeeTimes")

// Date-flexibility window options. Display only - this builder visualises an
// itinerary, it doesn't price anything, so flexibility is shown for context.
const FLEX_OPTIONS = {
	0: "None",
	1: "+/- 1 week",
	2: "+/- 2 weeks",
	3: "+/- 3 weeks",
	4: "+/- 4 weeks",
}

// Per-round buggy status. "included" = covered by the package (no extra charge),
// "charged" = we add a priced cost line, "local" = the client pays at the course.
const BUGGY_STATUS_OPTIONS = {
	included: "Included",
	charged: "Charged",
	local: "Pay locally",
}
// Buggy price entry (when "charged") shares the currency picker with the
// flight/transfer price fields.
const CURRENCY_OPTIONS = { gbp: "GBP (£)", eur: "EUR (€)", usd: "USD ($)" }

/**
 * Resolve a preview image exactly the way the "manage my booking" portal does:
 * the portal reads the supplier hotel/course's own image subcollection
 * (`hotels/{id}/images` / `courses/{id}/images`) and uses the first
 * `public_url`. A CMS_holidays/CMS_courses doc links to that supplier doc via
 * `bs2_id_hotel` / `bs2_id_course`. We fall back to the CMS media gallery only
 * when there's no linked supplier image.
 */
// Only absolute http(s) URLs render; legacy relative paths (e.g. "/site/...")
// resolve against the app domain and 404, so they're filtered out.
const isDisplayableImage = (url) => typeof url === "string" && /^https?:\/\//i.test(url)

// A blank flight leg (one-way = 1, return = 2). Carrier, luggage and golf bags
// live per leg (the return can differ from the outbound).
const emptyFlightLeg = () => ({ carrier: "", carrierCode: "", fromAirport: "", toAirport: "", depMillis: 0, arrMillis: 0, luggage: [] })

// Default occupancy ("sleeps") inferred from a room type the first time it's
// picked. Room rates are per person, so a room costs rate x sleeps; the agent
// can override this per room (suites / villas / apartments can't be guessed
// reliably from the type name, so we default the ambiguous ones to 2).
const defaultSleeps = (key) => {
	if (typeof key !== "string") return 2
	if (key.includes("single")) return 1
	if (key.includes("twin") || key.includes("double")) return 2
	if (key.includes("triple") || key.includes("three")) return 3
	if (key.includes("quad") || key.includes("four")) return 4
	if (key.includes("five")) return 5
	if (key.includes("six")) return 6
	if (key.includes("seven")) return 7
	return 2
}

// Deep-strip `undefined` (which Firestore rejects) before saving a draft, while
// leaving everything else intact. Crucially this preserves Firestore Timestamp
// instances (the price windows' start_date/end_date) - a JSON round-trip would
// flatten them to plain maps and lose the `.seconds` the date matching reads.
// Only plain objects are recursed; Timestamps and primitives pass through as-is.
const sanitiseForFirestore = (value) => {
	if (Array.isArray(value)) return value.map(sanitiseForFirestore)
	if (value && typeof value === "object" && Object.getPrototypeOf(value) === Object.prototype) {
		const out = {}
		Object.keys(value).forEach((key) => {
			if (value[key] !== undefined) out[key] = sanitiseForFirestore(value[key])
		})
		return out
	}
	return value
}

// A hotel/round basket item carries its CMS price windows - the full room, board
// and fee pricing matrix for every date range. That's by far the heaviest thing
// on the item, and with auto-added connected courses it multiplies fast: enough
// to push a quote doc over Firestore's 1 MiB limit. The windows are always
// re-fetchable from the CMS by `cmsId`, so we drop them before saving and refetch
// them when the draft is reopened (see the restore effect in CreateQuoteModal).
// The single `selectedPackage` window is kept - it's tiny and drives the costing.
const stripWindowsForSave = (item) => {
	if (!item || item.priceWindows === undefined) return item
	const { priceWindows, ...rest } = item
	return rest
}

// A round carries a single tee DATE plus zero or more tee TIMES on that date
// (each stored as a full datetime on the date). Combine a date with a picked
// time, keeping the date's day and the time's hour/minute. Falsy time -> 0
// (an empty row); falsy date -> the time as-is (defensive).
const combineDateAndTime = (dateMillis, timeMillis) => {
	if (!timeMillis) return 0
	if (!dateMillis) return timeMillis
	const picked = moment(timeMillis)
	return moment(dateMillis).startOf("day").hour(picked.hour()).minute(picked.minute()).second(0).millisecond(0).valueOf()
}

// Back-compat: older drafts stored a round's tee times as a flat array of full
// datetimes with no separate date. Derive `teeDate` from the earliest time and
// drop empty (0) entries, so the builder always works in the new shape. A round
// that already has a `teeDate` key is left untouched.
const normaliseRoundTimes = (item) => {
	if (!item || item.type !== "round") return item
	if (Object.prototype.hasOwnProperty.call(item, "teeDate")) return item
	const times = (item.teeTimes || []).filter((tee) => tee > 0).sort((a, b) => a - b)
	const teeDate = times.length ? moment(times[0]).startOf("day").valueOf() : null
	return { ...item, teeDate, teeTimes: times }
}

// A "charged" buggy on a round becomes its own cost line (its own currency), so
// a non-GBP buggy converts correctly via its currency. The agent enters a price
// PER BUGGY plus a quantity (the number of buggies the group needs); the line
// total is simply price x quantity, spread across the group for the per-person
// figure. Returns null unless the buggy is enabled, charged, and has both a
// positive price and a positive quantity.
const buggyCostLine = (item, groupSize) => {
	const buggies = normaliseBuggies(item.buggies)
	if (!buggies.enabled || buggies.status !== "charged") return null
	const price = Number(buggies.price) || 0
	const quantity = Number(buggies.quantity) || 0
	if (price <= 0 || quantity <= 0) return null
	const lineTotal = price * quantity
	return {
		uid: `${item.uid}-buggies`,
		name: `${item.title} · buggies`,
		kind: "buggies",
		currency: buggies.currency || "gbp",
		priced: true,
		perPerson: groupSize > 0 ? lineTotal / groupSize : 0,
		lineTotal,
	}
}

// The trip's travel window, used to keep tee dates valid. Spans the earliest
// hotel check-in to the latest checkout across every stay; falls back to the
// trip travel date -> travel date + nights when no hotel has a check-in yet.
const roundTravelWindow = (items, fallbackTravelMillis, fallbackNights) => {
	const stays = (items || []).filter((item) => item.type === "hotel" && item.checkInMillis)
	if (stays.length === 0) {
		if (!fallbackTravelMillis) return null
		return {
			start: moment(fallbackTravelMillis).startOf("day").valueOf(),
			end: moment(fallbackTravelMillis).add(Number(fallbackNights) || 0, "days").endOf("day").valueOf(),
		}
	}
	let start = Infinity
	let end = -Infinity
	stays.forEach((hotel) => {
		const checkIn = moment(hotel.checkInMillis).startOf("day").valueOf()
		const checkOut = moment(hotel.checkInMillis).add(Number(hotel.nights) || 0, "days").endOf("day").valueOf()
		if (checkIn < start) start = checkIn
		if (checkOut > end) end = checkOut
	})
	return { start, end }
}

// Clear any round whose tee date now falls outside the travel window (e.g. after
// a check-in moved). Returns the updated items + how many rounds were cleared so
// the caller can surface a "N tee times were cleared" notice.
const clampRoundsToWindow = (items, fallbackTravelMillis, fallbackNights) => {
	const window = roundTravelWindow(items, fallbackTravelMillis, fallbackNights)
	if (!window) return { items, cleared: 0 }
	let cleared = 0
	const next = items.map((item) => {
		if (item.type !== "round" || !item.teeDate) return item
		if (item.teeDate < window.start || item.teeDate > window.end) {
			cleared += 1
			return { ...item, teeDate: null, teeTimes: [] }
		}
		return item
	})
	return { items: next, cleared }
}

// Load the (currently-valid) notes + discounts held against a CMS hotel/course,
// mirroring the /quoting search. Each carries a [start_date, end_date] window of
// Firestore Timestamps; the card filters them to the item's own dates. Best
// effort - a failure just yields none.
const loadNotesAndDiscounts = async (collectionName, objectID) => {
	const startOfToday = moment().startOf("day").toDate()
	const result = { notes: [], discounts: [] }
	try {
		const [noteDocs, discountDocs] = await Promise.all([
			db.collection(`${collectionName}/${objectID}/notes`).where("end_date", ">=", startOfToday).get(),
			db.collection(`${collectionName}/${objectID}/discounts`).where("end_date", ">=", startOfToday).get(),
		])
		// Project to only the fields the card + cost maths need - the raw CMS docs
		// carry extra fields we'd otherwise persist on every basket item.
		result.notes = noteDocs.docs.map((doc) => {
			const data = doc.data() || {}
			return { id: doc.id, text: data.text || "", start_date: data.start_date, end_date: data.end_date }
		})
		result.discounts = discountDocs.docs.map((doc) => {
			const data = doc.data() || {}
			return { id: doc.id, title: data.title || "", type: data.type || "", value: data.value, start_date: data.start_date, end_date: data.end_date }
		})
	} catch (err) {
		// Non-fatal - the card just shows no notes/discounts.
	}
	return result
}

// True when a note/discount's [start_date, end_date] window overlaps the item's
// own date window. With no item window yet (no date chosen) everything shows, so
// the agent can see what's available before picking a date.
const overlapsWindow = (entry, fromMillis, toMillis) => {
	if (!fromMillis || !toMillis) return true
	const start = (entry.start_date?.seconds || 0) * 1000
	const end = (entry.end_date?.seconds || 0) * 1000
	if (!start || !end) return true
	return start <= toMillis && end >= fromMillis
}

// Human label for a discount chip (its effect on the price).
const discountSummary = (discount) => {
	if (discount.type === "percent") return `${discount.value}% off`
	if (discount.type === "pax") return `1 in ${discount.value} free`
	return ""
}

// Apply chosen discounts to a per-person figure, mirroring calculatePackageCost's
// discount maths (which the hotel line can't route through, as it sums a room mix
// the engine doesn't model). Rounds keep using the engine's own handling.
const applyPerPersonDiscounts = (perPerson, discountsChosen, groupSize) => {
	let total = perPerson
	;(discountsChosen || []).forEach(({ type, value }) => {
		if (type === "pax") {
			const per = parseInt(value)
			if (per > 0 && groupSize > 0) {
				const goingFree = Math.floor(groupSize / per)
				const adjusted = (total * (groupSize - goingFree)) / groupSize
				if (!isNaN(adjusted)) total = Math.round(adjusted)
			}
		} else if (type === "percent") {
			const factor = 1 - parseFloat(value) / 100
			if (!isNaN(factor)) total = Math.round(total * factor)
		}
	})
	return total
}

const resolvePortalImage = async (collectionName, bs2Id, media) => {
	if (bs2Id) {
		try {
			const imgs = await db.collection(`${collectionName}/${bs2Id}/images`).limit(20).get()
			const valid = imgs.docs.map((imageDoc) => imageDoc.data()?.public_url).find(isDisplayableImage)
			if (valid) return valid
		} catch (err) {
			// Fall through to the CMS media gallery below.
		}
	}
	// CMS media fallback, also filtered to displayable URLs.
	return [media?.primary_image, ...(media?.images || [])].find(isDisplayableImage) || ""
}

/**
 * "New Quote" entry point for the enquiry Quotes tab. Renders the toolbar
 * button; the heavy lifting lives in the full-screen modal, which is only
 * mounted while open so the Algolia client / listeners aren't created until
 * the agent actually starts a quote.
 *
 * Nothing here writes to the database - it is purely a visual itinerary
 * builder seeded from the enquiry.
 */
export default function NewQuote({ seed, onSaved, onRequestSend }) {
	const [open, setOpen] = useState(false)

	return (
		<>
			<div className="new-quote-toolbar">
				<Button label="New Quote" icon={<Plus size={16} />} onClick={() => setOpen(true)} />
			</div>

			{open && (
				<CreateQuoteModal
					seed={seed}
					enquiryID={seed?.enquiryID}
					onSaved={onSaved}
					onClose={() => setOpen(false)}
					onSaveAndSend={
						onRequestSend
							? (quote) => {
									setOpen(false)
									onRequestSend(quote)
							  }
							: undefined
					}
				/>
			)}
		</>
	)
}

/**
 * Full-screen "Create new Quote" modal: enquiry-seeded parameters along the
 * top, a search-and-basket on the left, and a live itinerary preview on the
 * right that mirrors the client-facing /mybooking timeline.
 *
 * Opens blank from the "New Quote" button (seeded by the enquiry), or
 * pre-filled from a saved draft (`initialDraft` + its `draftId`), in which case
 * saving updates that same draft rather than creating a new one.
 */
export function CreateQuoteModal({ seed, enquiryID, draftId: initialDraftId = null, initialDraft = null, readOnly = false, onClose, onSaved, onSaveAndSend }) {
	// When editing a draft we rehydrate from its stored state; otherwise we seed
	// from the enquiry. Drafts persist under enquiries/{id}/quotes (is_draft).
	const resolvedEnquiryID = enquiryID || seed?.enquiryID || initialDraft?.enquiry || ""

	// Enquiry-seeded / draft-restored parameters.
	const [groupSize, setGroupSize] = useState(initialDraft ? initialDraft.group_size ?? "" : seed?.groupSize ?? "")
	const [travelMillis, setTravelMillis] = useState(initialDraft ? initialDraft.travel_millis || 0 : seed?.checkInMillis || 0)
	const [flexWeeks, setFlexWeeks] = useState(initialDraft ? String(initialDraft.flex_weeks ?? 0) : "0")
	const [nights, setNights] = useState(initialDraft ? initialDraft.nights ?? "" : seed?.nights ?? "")
	const [rounds, setRounds] = useState(initialDraft ? initialDraft.rounds ?? "" : seed?.rounds ?? "")

	// The five trip params collapse into a one-line summary to save vertical space;
	// expand to edit. Start expanded only for a blank new quote (nothing seeded).
	const [paramsOpen, setParamsOpen] = useState(() => !(travelMillis || groupSize || nights || rounds))

	// The basket: hotels (with room-type picks + a check-in date) and golf
	// rounds (one round per add, each with its own date + tee time).
	const [basket, setBasket] = useState(initialDraft ? (initialDraft.basket || []).map(normaliseRoundTimes) : [])
	const [adding, setAdding] = useState(false)

	// The basket item (by uid) whose package-selection modal is open.
	const [packageModalUid, setPackageModalUid] = useState(null)

	// When a hotel package with included rounds spans several connected courses,
	// this holds { hotelUid, rounds, refs } so the agent can allocate the rounds
	// across the courses before they're added.
	const [coursePicker, setCoursePicker] = useState(null)

	// The flight/transfer item (by uid) being edited in its own modal.
	const [editItemUid, setEditItemUid] = useState(null)

	// Whether the read-only "view enquiry" modal is open.
	const [enquiryViewOpen, setEnquiryViewOpen] = useState(false)

	// Whether the client-facing "preview" overlay is open (renders the public
	// quote page from the live builder state - no DB write, nothing bookable).
	const [previewOpen, setPreviewOpen] = useState(false)

	// The hotel/round basket item (by uid) open in its full edit modal.
	const [editBasketUid, setEditBasketUid] = useState(null)

	// Count of rounds whose tee dates were cleared by the last check-in change,
	// shown as a brief notice then auto-dismissed.
	const [teeResetNotice, setTeeResetNotice] = useState(0)
	useEffect(() => {
		if (!teeResetNotice) return undefined
		const timer = setTimeout(() => setTeeResetNotice(0), 6000)
		return () => clearTimeout(timer)
	}, [teeResetNotice])

	// Drafts are saved without their bulky CMS price windows (see stripWindowsForSave),
	// so when we reopen one we re-fetch the windows for each hotel/round by `cmsId`.
	// That's what lets the package picker list and date-filter packages again on edit.
	useEffect(() => {
		if (!initialDraft) return undefined
		let cancelled = false
		const restoreWindows = async () => {
			const needing = (initialDraft.basket || []).filter(
				(item) => (item.type === "hotel" || item.type === "round") && item.cmsId && (!item.priceWindows || item.priceWindows.length === 0)
			)
			if (needing.length === 0) return
			const byUid = {}
			await Promise.all(
				needing.map(async (item) => {
					const collection = item.type === "hotel" ? "CMS_holidays" : "CMS_courses"
					const priceType = item.type === "hotel" ? "resort" : "course"
					try {
						const windowDocs = await db.collection(`${collection}/${item.cmsId}/price_windows`).get()
						byUid[item.uid] = windowDocs.docs.map((windowDoc) => ({ id: windowDoc.id, price_type: priceType, ...windowDoc.data() }))
					} catch (err) {
						// Non-fatal - the package picker just stays empty until the item is re-added.
						console.error("Could not restore price windows for quote item:", err)
					}
				})
			)
			if (cancelled) return
			setBasket((current) => current.map((item) => (byUid[item.uid] ? { ...item, priceWindows: byUid[item.uid] } : item)))
		}
		restoreWindows()
		return () => {
			cancelled = true
		}
		// Runs once on mount for a reopened draft; the deps it reads (initialDraft, db)
		// are stable for the modal's lifetime.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// Manual exchange rates per currency (resort-currency units per £1), e.g.
	// { eur: "1.15", usd: "1.30" }. Used to convert each priced line into GBP.
	const [exchangeRates, setExchangeRates] = useState(initialDraft ? initialDraft.exchange_rates || {} : {})
	const setRate = (currency, value) => setExchangeRates((current) => ({ ...current, [currency]: value }))

	// Optional total sell price for the booking; drives the margin preview.
	const [sellAt, setSellAt] = useState(initialDraft && initialDraft.sell_at != null ? String(initialDraft.sell_at) : "")
	// Whether the agent has taken over the sell price. Until they do, it tracks the
	// cost-per-person (pre-filled so the margin fields are live from the start); a
	// restored draft that already carries a sell price counts as taken over.
	const [sellTouched, setSellTouched] = useState(Boolean(initialDraft && initialDraft.sell_at != null))
	const handleSellChange = (value) => {
		setSellTouched(true)
		setSellAt(value)
	}

	// Split sell pricing. When on, the single per-person sell price is replaced by
	// a set of per-person "tiers" (e.g. sharing vs single-occupancy), each with a
	// head count - so a 4-ball where 2 share a twin and 2 take singles can be
	// priced on two lines. Stored as an array so more tiers can be added later;
	// the save denormalises a blended per-person figure into `sell_at` for cards.
	const [splitSell, setSplitSell] = useState(Boolean(initialDraft && initialDraft.sell_split))
	const [sellLines, setSellLines] = useState(
		initialDraft && Array.isArray(initialDraft.sell_lines) && initialDraft.sell_lines.length
			? initialDraft.sell_lines.map((line) => ({
					price: line.price != null ? String(line.price) : "",
					qty: line.qty != null ? String(line.qty) : "",
			  }))
			: [
					{ price: "", qty: "" },
					{ price: "", qty: "" },
			  ]
	)
	const setSellLine = (index, field, value) => setSellLines((current) => current.map((line, i) => (i === index ? { ...line, [field]: value } : line)))

	// Draft persistence: the doc id once saved (so re-saves update it), plus
	// in-flight / confirmation / error state for the save button.
	const [draftId, setDraftId] = useState(initialDraftId)
	const [saving, setSaving] = useState(false)
	const [savedAt, setSavedAt] = useState(0)
	const [saveError, setSaveError] = useState("")

	// Stable, render-independent ids for basket rows. When restoring a draft we
	// start the counter past the highest existing uid so new rows never collide.
	const idRef = useRef((initialDraft?.basket || []).reduce((max, item) => {
		const n = parseInt(String(item.uid || "").replace(/[^0-9]/g, ""), 10)
		return isNaN(n) ? max : Math.max(max, n + 1)
	}, 0))
	const newId = () => `nq-${idRef.current++}`

	const searchClient = useMemo(() => algoliasearch("HY764WYKNV", "059a9cd79a33ec2b2822acf2ffdfd4c9"), [])

	// --- Basket mutations -------------------------------------------------
	const updateItem = (uid, patch) => setBasket((current) => current.map((item) => (item.uid === uid ? { ...item, ...patch } : item)))
	// Removing a hotel also removes any rounds auto-added from its package (T7).
	const removeItem = (uid) => setBasket((current) => current.filter((item) => item.uid !== uid && item.autoFromHotelUid !== uid))
	// Duplicate a configured round exactly (package, fee band, buggies, discounts)
	// one day later: the tee date and every tee time shift forward a day, keeping
	// the times of day. The copy slots in directly under its source. It's always a
	// manual row - dropping autoFromHotelUid means re-picking the source hotel's
	// package never sweeps the copy away with the auto-added rounds.
	const copyRound = (uid) =>
		setBasket((current) => {
			const index = current.findIndex((item) => item.uid === uid)
			if (index === -1) return current
			const { autoFromHotelUid, ...source } = current[index]
			const dayLater = (millis) => (millis ? moment(millis).add(1, "day").valueOf() : millis)
			const copy = {
				...source,
				uid: newId(),
				teeDate: dayLater(source.teeDate),
				teeTimes: (source.teeTimes || []).map(dayLater),
			}
			return [...current.slice(0, index + 1), copy, ...current.slice(index + 1)]
		})
	const toggleRoom = (uid, key) =>
		setBasket((current) =>
			current.map((item) => {
				if (item.uid !== uid) return item
				const has = item.selectedRooms.some((room) => room.key === key)
				return {
					...item,
					selectedRooms: has ? item.selectedRooms.filter((room) => room.key !== key) : [...item.selectedRooms, { key, qty: 1, sleeps: defaultSleeps(key) }],
				}
			})
		)
	const setRoomQty = (uid, key, qty) =>
		setBasket((current) =>
			current.map((item) => (item.uid === uid ? { ...item, selectedRooms: item.selectedRooms.map((room) => (room.key === key ? { ...room, qty } : room)) } : item))
		)
	const setRoomSleeps = (uid, key, sleeps) =>
		setBasket((current) =>
			current.map((item) => (item.uid === uid ? { ...item, selectedRooms: item.selectedRooms.map((room) => (room.key === key ? { ...room, sleeps } : room)) } : item))
		)
	const setBoard = (uid, key) => setBasket((current) => current.map((item) => (item.uid === uid ? { ...item, selectedBoard: key } : item)))

	// Toggle a resort/course discount on the item. We store only the fields the
	// cost maths needs (id/type/value) plus a title for the chosen-discount list.
	const toggleDiscount = (uid, discount) =>
		setBasket((current) =>
			current.map((item) => {
				if (item.uid !== uid) return item
				const chosen = item.discountsChosen || []
				const has = chosen.some((entry) => entry.id === discount.id)
				return {
					...item,
					discountsChosen: has
						? chosen.filter((entry) => entry.id !== discount.id)
						: [...chosen, { id: discount.id, type: discount.type, value: discount.value, title: discount.title }],
				}
			}),
		)

	// --- Flights & transfers ----------------------------------------------
	// Default flight leg dates. Prefer the chosen hotels: the outbound departs on
	// the earliest check-in, the return on the latest checkout (check-in + that
	// hotel's nights), so the flight tracks the actual stay across one or more
	// hotels. Falls back to the trip travel date (+ nights for the return) when no
	// hotel has a check-in yet, and 0 (manual; the picker opens on today) when
	// there's nothing to go on. Departure + arrival seed to the same day so the
	// agent only fills in the times.
	const flightLegDefaultMillis = (legIndex) => {
		const stays = basket.filter((item) => item.type === "hotel" && item.checkInMillis)
		if (stays.length > 0) {
			if (legIndex === 0) {
				return Math.min(...stays.map((hotel) => moment(hotel.checkInMillis).startOf("day").valueOf()))
			}
			return Math.max(...stays.map((hotel) => moment(hotel.checkInMillis).add(Number(hotel.nights) || 0, "days").startOf("day").valueOf()))
		}
		if (!travelMillis) return 0
		return moment(travelMillis).add(legIndex === 0 ? 0 : Number(nights) || 0, "days").valueOf()
	}

	const addFlight = () => {
		const uid = newId()
		const outbound = flightLegDefaultMillis(0)
		const inbound = flightLegDefaultMillis(1)
		setBasket((current) => [
			...current,
			{
				uid,
				type: "flight",
				tripType: "return",
				legs: [
					{ ...emptyFlightLeg(), depMillis: outbound, arrMillis: outbound },
					{ ...emptyFlightLeg(), depMillis: inbound, arrMillis: inbound },
				],
				currency: "gbp",
				priceMode: "per_person",
				price: "",
			},
		])
		setEditItemUid(uid)
	}
	const setFlightTripType = (uid, tripType) =>
		setBasket((current) =>
			current.map((item) => {
				if (item.uid !== uid) return item
				let legs = item.legs
				if (tripType === "return" && legs.length < 2) {
					const inbound = flightLegDefaultMillis(1)
					legs = [...legs, { ...emptyFlightLeg(), depMillis: inbound, arrMillis: inbound }]
				}
				if (tripType === "oneway") legs = legs.slice(0, 1)
				return { ...item, tripType, legs }
			})
		)
	const setFlightLeg = (uid, index, patch) =>
		setBasket((current) => current.map((item) => (item.uid === uid ? { ...item, legs: item.legs.map((leg, i) => (i === index ? { ...leg, ...patch } : leg)) } : item)))

	const addTransfer = () => {
		const uid = newId()
		setBasket((current) => [...current, { uid, type: "transfer", transferType: "airport", hotelUid: "", courseUid: "", courseUids: [], coursePrices: {}, airport: "", airportLabel: "", currency: "gbp", priceMode: "total", price: "" }])
		setEditItemUid(uid)
	}

	// A Euro Tunnel item: a net price to us in GBP or EUR (priced per booking, so
	// a group total) plus, like a flight, a one-way/return trip type with a
	// crossing datetime per direction - that's what places it on the timeline.
	// Crossing dates default off the trip the same way flight legs do.
	const addEuroTunnel = () => {
		const uid = newId()
		const outbound = flightLegDefaultMillis(0)
		const inbound = flightLegDefaultMillis(1)
		setBasket((current) => [...current, { uid, type: "eurotunnel", tripType: "return", outboundMillis: outbound, returnMillis: inbound, currency: "gbp", price: "" }])
		setEditItemUid(uid)
	}

	// Changing the trip travel date invalidates the date-specific package
	// selections, so clear them all and snap hotel check-ins to the new date.
	const handleTravelDateChange = (millis) => {
		setTravelMillis(millis)
		setBasket((current) => {
			const snapped = current
				// Every hotel's package is cleared here, so its auto-added connected
				// courses lose their justification - drop them rather than leave
				// orphaned, package-less rounds on the itinerary.
				.filter((item) => !item.autoFromHotelUid)
				.map((item) =>
					item.type === "hotel"
						? { ...item, checkInMillis: millis, selectedPackage: null, selectedRooms: [], selectedBoard: "" }
						: { ...item, selectedPackage: null, chosenFeeType: "" }
				)
			// The whole trip moved, so tee dates set against the old window are
			// almost certainly stale - clear any that now fall outside it.
			return clampRoundsToWindow(snapped, millis, nights).items
		})
	}

	// A single hotel's check-in changed (independently of the trip travel date).
	// Recompute the travel window across all stays and clear any round whose tee
	// date now falls outside it, surfacing a brief notice when that happens.
	const handleHotelCheckInChange = (uid, millis) => {
		const updated = basket.map((item) => (item.uid === uid ? { ...item, checkInMillis: millis } : item))
		const { items, cleared } = clampRoundsToWindow(updated, travelMillis, nights)
		setBasket(items)
		if (cleared > 0) setTeeResetNotice(cleared)
	}

	// --- Real packages -----------------------------------------------------
	// Attach a chosen package to its item. For hotels this sets the nights and
	// pre-selects the package's base room/board (the package then drives the
	// room/board pickers). For courses it stamps the chosen green-fee band.
	// `rooms` (optional) is a pre-built room mix - the custom-package builder
	// passes the agent's chosen room types with their quantities/sleeps so the
	// whole mix lands on the item, not just a single base room. Loaded packages
	// omit it and fall back to pre-selecting the package's base room.
	const selectPackage = (uid, { window, feeType, rooms }) =>
		setBasket((current) =>
			current.map((item) => {
				if (item.uid !== uid) return item

				if (item.type === "hotel") {
					const baseRoom = (window.room_types || []).find((room) => room.base) || (window.room_types || [])[0]
					const baseBoard = (window.board_types || []).find((board) => board.base) || (window.board_types || [])[0]
					const nights = window.type === "per_night" ? Number(item.nights) || Number(window.nights) || 0 : Number(window.nights) || 0
					return {
						...item,
						selectedPackage: window,
						nights,
						selectedRooms:
							rooms && rooms.length
								? rooms.map((room) => ({ key: room.key, qty: Number(room.qty) || 1, sleeps: Number(room.sleeps) || defaultSleeps(room.key) }))
								: baseRoom
								? [{ key: baseRoom.type, qty: 1, sleeps: defaultSleeps(baseRoom.type) }]
								: [],
						selectedBoard: baseBoard ? baseBoard.type : "",
					}
				}

				return { ...item, selectedPackage: window, chosenFeeType: feeType || "" }
			})
		)

	// --- Tee date + times (one date per round, several times as options) ---
	// Set the round's date and re-stamp any existing times onto the new day so
	// the times-of-day are preserved when the date changes.
	const setTeeDate = (uid, millis) =>
		setBasket((current) =>
			current.map((item) => {
				if (item.uid !== uid) return item
				const teeDate = millis ? moment(millis).startOf("day").valueOf() : null
				const teeTimes = (item.teeTimes || []).map((tee) => (tee && teeDate ? combineDateAndTime(teeDate, tee) : tee))
				return { ...item, teeDate, teeTimes }
			}),
		)
	// Append an empty time row (filled in via the time picker). Guarded in the UI
	// so it's only reachable once a date is set.
	const addTeeTime = (uid) => setBasket((current) => current.map((item) => (item.uid === uid ? { ...item, teeTimes: [...(item.teeTimes || []), 0] } : item)))
	// A picked time carries an arbitrary date (the time picker has no calendar),
	// so stamp it onto the round's date.
	const setTeeTime = (uid, index, picked) =>
		setBasket((current) =>
			current.map((item) => {
				if (item.uid !== uid) return item
				const combined = combineDateAndTime(item.teeDate, picked)
				return { ...item, teeTimes: item.teeTimes.map((tee, i) => (i === index ? combined : tee)) }
			}),
		)
	const removeTeeTime = (uid, index) =>
		setBasket((current) => current.map((item) => (item.uid === uid ? { ...item, teeTimes: item.teeTimes.filter((_, i) => i !== index) } : item)))
	// Add/remove a specific clock time ("HH:MM") on the round, stamped onto its tee
	// date. The connector's live-availability picker speaks in HH:MM, so toggling a
	// slot either appends it as an option or removes the matching one — letting the
	// list reflect what's already been added without duplicating times.
	const toggleConnectorTeeTime = (uid, hhmm) =>
		setBasket((current) =>
			current.map((item) => {
				if (item.uid !== uid || !item.teeDate) return item
				const [hour, minute] = hhmm.split(":").map((part) => Number(part))
				const millis = moment(item.teeDate).startOf("day").hour(hour).minute(minute).second(0).millisecond(0).valueOf()
				const times = item.teeTimes || []
				const existing = times.findIndex((tee) => tee && moment(tee).format("HH:mm") === hhmm)
				const teeTimes = existing >= 0 ? times.filter((_, i) => i !== existing) : [...times, millis]
				return { ...item, teeTimes }
			}),
		)

	// --- Adding from search -----------------------------------------------
	const handleResortChosen = async (objectID, hit) => {
		setAdding(true)
		try {
			const data = (await db.doc(`CMS_holidays/${objectID}`).get()).data() || {}

			// Store the hotel's real price windows so the package modal can list
			// and filter them. Room/board options are driven by the selected
			// package, so we don't pre-compute a union here.
			const windowDocs = await db.collection(`CMS_holidays/${objectID}/price_windows`).get()
			const priceWindows = windowDocs.docs.map((windowDoc) => ({ id: windowDoc.id, price_type: "resort", ...windowDoc.data() }))

			// The resort's linked courses (same lookup the /quoting page uses) - the
			// reliable source of course names for the "included golf" summary.
			let linkedCourses = []
			let linkedCourseRefs = []
			if (data.page_id) {
				try {
					const courseDocs = await db.collection("CMS_courses").where("resort_ids", "array-contains", Number(data.page_id)).get()
					// Keep the doc ids (not just titles) so a package's included rounds
					// can be auto-added as real, priceable round items (T7).
					linkedCourseRefs = courseDocs.docs.map((courseDoc) => ({ id: courseDoc.id, title: courseDoc.data().title })).filter((ref) => ref.title)
					linkedCourses = linkedCourseRefs.map((ref) => ref.title)
				} catch (err) {
					// Non-fatal - the summary just falls back to the rounds count.
				}
			}

			const image = await resolvePortalImage("hotels", data.bs2_id_hotel, data.media)

			// Notes + discounts held against the resort (surfaced on the card).
			const { notes, discounts } = await loadNotesAndDiscounts("CMS_holidays", objectID)

			// The supplier hotel doc carries an optional check-in time of day, stored
			// as a "h:mmA" string (e.g. "3:00PM"); show it on the card when present.
			let checkInTime = ""
			if (data.bs2_id_hotel) {
				try {
					const supplierDoc = await db.doc(`hotels/${data.bs2_id_hotel}`).get()
					const rawCheckIn = supplierDoc.data()?.check_in
					if (rawCheckIn) checkInTime = moment(rawCheckIn, "h:mmA").format("HH:mm")
				} catch (err) {
					// Non-fatal - the card just omits the from-time.
				}
			}

			setBasket((current) => [
				...current,
				{
					uid: newId(),
					type: "hotel",
					cmsId: objectID,
					title: hit.title || data.title || "Hotel",
					image,
					currency: data.currency || "gbp",
					priceWindows,
					linkedCourses,
					linkedCourseRefs,
					checkInTime,
					notes,
					discounts,
					discountsChosen: [],
					selectedPackage: null,
					selectedRooms: [],
					selectedBoard: "",
					checkInMillis: travelMillis || 0,
					nights: Number(nights) || 0,
				},
			])
		} catch (err) {
			console.error("Could not add hotel to quote:", err)
		} finally {
			setAdding(false)
		}
	}

	// Build a fully-loaded round basket item for a CMS course (price windows,
	// image, currency, notes/discounts). Shared by the manual course search and
	// the package auto-add (T7); the caller appends it to the basket.
	const loadRoundItem = async (objectID, title) => {
		const data = (await db.doc(`CMS_courses/${objectID}`).get()).data() || {}
		const image = await resolvePortalImage("courses", data.bs2_id_course, data.media)
		const windowDocs = await db.collection(`CMS_courses/${objectID}/price_windows`).get()
		const priceWindows = windowDocs.docs.map((windowDoc) => ({ id: windowDoc.id, price_type: "course", ...windowDoc.data() }))
		const { notes, discounts } = await loadNotesAndDiscounts("CMS_courses", objectID)
		return {
			uid: newId(),
			type: "round",
			cmsId: objectID,
			title: title || data.title || "Golf course",
			image,
			currency: data.currency || "gbp",
			priceWindows,
			notes,
			discounts,
			discountsChosen: [],
			selectedPackage: null,
			chosenFeeType: "",
			// One round per add: a single tee DATE (set later) plus zero or more
			// tee times on it, shown to the client as options.
			teeDate: null,
			teeTimes: [],
			// Buggy provision for this round (off by default).
			buggies: normaliseBuggies(),
		}
	}

	const handleCourseChosen = async (objectID, hit) => {
		setAdding(true)
		try {
			const item = await loadRoundItem(objectID, hit.title)
			setBasket((current) => [...current, item])
		} catch (err) {
			console.error("Could not add course to quote:", err)
		} finally {
			setAdding(false)
		}
	}

	// Build the resort's connected courses as real round items for a hotel package
	// with included rounds (T7). `orderedRefs` is one ref PER round (length = the
	// included-rounds count); each becomes a tagged round item with its tee date
	// spread evenly across the stay. Always clears this hotel's previous auto
	// rounds first, so re-picking a package doesn't pile them up.
	const addHotelRounds = async (hotel, orderedRefs) => {
		setBasket((current) => current.filter((item) => item.autoFromHotelUid !== hotel.uid))
		// Defensive cap: round counts are domain-bounded (a handful), but never add
		// an absurd number (each carries a full price-window load).
		const refs = (orderedRefs || []).slice(0, 12)
		if (refs.length === 0) return
		const checkInDay = hotel.checkInMillis ? moment(hotel.checkInMillis).startOf("day").valueOf() : null
		const stayNights = Number(hotel.nights) || 0
		const total = refs.length
		// Even day offset for the i-th round across the stay. With as many rounds as
		// nights this is one per day; fewer rounds spread out; it never lands on the
		// checkout day. Falls back to the check-in day when nights aren't set.
		const teeDateFor = (index) =>
			checkInDay !== null && stayNights > 0 ? moment(checkInDay).add(Math.round((index * stayNights) / total), "days").valueOf() : checkInDay
		setAdding(true)
		try {
			const newRounds = []
			for (let index = 0; index < refs.length; index++) {
				const ref = refs[index]
				// eslint-disable-next-line no-await-in-loop
				const round = await loadRoundItem(ref.id, ref.title)
				round.autoFromHotelUid = hotel.uid
				round.teeDate = teeDateFor(index)
				newRounds.push(round)
			}
			// Only append if the hotel is still in the basket (it may have been
			// removed, or its package re-picked, while these loaded).
			setBasket((current) => (current.some((item) => item.uid === hotel.uid) ? [...current, ...newRounds] : current))
		} catch (err) {
			console.error("Could not add connected courses:", err)
		} finally {
			setAdding(false)
		}
	}

	// Legacy auto-spread: cycle the connected courses up to N rounds. Used when
	// there's no real choice (a single connected course) or the agent skips the
	// allocation picker.
	const cycledRefs = (refs, count) => Array.from({ length: count }, (_, index) => refs[index % refs.length])

	// Decide how a hotel package's included rounds are populated. No rounds / no
	// connected courses → clear and stop. One connected course → auto-fill it ×N.
	// Several → open the picker so the agent allocates the N rounds across courses.
	const openHotelRounds = (hotel, pkg) => {
		const refs = hotel.linkedCourseRefs || []
		const roundsNeeded = Math.min(Number(pkg?.rounds) || 0, 12)
		if (roundsNeeded <= 0 || refs.length === 0) {
			setBasket((current) => current.filter((item) => item.autoFromHotelUid !== hotel.uid))
			return
		}
		if (refs.length === 1) {
			addHotelRounds(hotel, cycledRefs(refs, roundsNeeded))
			return
		}
		setCoursePicker({ hotelUid: hotel.uid, rounds: roundsNeeded, refs })
	}

	// --- Algolia search box + results (dropdown) --------------------------
	const SearchBox = ({ currentRefinement, refine }) => (
		<form noValidate role="search" onSubmit={(event) => event.preventDefault()}>
			<Input value={currentRefinement} icon={<SearchGlyph size={16} />} fullEvent={true} placeholder="Search hotels & courses..." onChange={(event) => refine(event.currentTarget.value)} />
		</form>
	)
	const CustomSearchBox = connectSearchBox(SearchBox)

	const ResortHits = connectHits(({ hits }) =>
		hits.length ? (
			<div className="new-quote-result-group">
				{hits.map((hit) => (
					<div key={hit.objectID} className="new-quote-result" onClick={() => handleResortChosen(hit.objectID, hit)}>
						<Badge label="Hotel" type="INFO" />
						<p>{hit.title}</p>
						<Plus size={14} />
					</div>
				))}
			</div>
		) : null
	)

	const CourseHits = connectHits(({ hits }) =>
		hits.length ? (
			<div className="new-quote-result-group">
				{hits.map((hit) => (
					<div key={hit.objectID} className="new-quote-result" onClick={() => handleCourseChosen(hit.objectID, hit)}>
						<Badge label="Course" type="POSITIVE" />
						<p>{hit.title}</p>
						<Plus size={14} />
					</div>
				))}
			</div>
		) : null
	)

	const ResortResults = connectStateResults(({ searchState }) => (searchState.query ? <ResortHits /> : null))
	const CourseResults = connectStateResults(({ searchState }) => (searchState.query ? <CourseHits /> : null))

	// --- Itinerary nodes (right preview), ordered by their assigned date ---
	// Both the builder and the public quote page render the same timeline via
	// the shared itinerary-timeline module (buildItineraryNodes + ItineraryTimeline).
	const nodes = useMemo(() => buildItineraryNodes(basket), [basket])

	// Hotels / courses already in the basket, for transfer endpoint pickers.
	const basketHotels = basket.filter((item) => item.type === "hotel").map((item) => ({ uid: item.uid, title: item.title }))
	const basketCourses = basket.filter((item) => item.type === "round").map((item) => ({ uid: item.uid, title: item.title }))
	const hotelTitleByUid = Object.fromEntries(basketHotels.map((hotel) => [hotel.uid, hotel.title]))
	const courseTitleByUid = Object.fromEntries(basketCourses.map((course) => [course.uid, course.title]))

	// Left basket ordering: pin the extras (flights / transfers / shuttles /
	// tunnel crossings) to the top, then a dashed divider, then the hotels & golf
	// rounds. Keeps the list scannable without reordering the underlying basket
	// (dates still drive the itinerary). Relative order within each group is
	// preserved.
	const basketExtras = basket.filter((item) => item.type === "flight" || item.type === "transfer" || item.type === "eurotunnel")
	const basketMains = basket.filter((item) => item.type === "hotel" || item.type === "round")

	// Meta line for the collapsed trip-params summary. Always shows group / nights
	// / rounds / date flexibility (even when zero/unset) so the summary is the full
	// trip at a glance; wraps rather than truncates so nothing gets clipped.
	const num = (value) => Number(value) || 0
	const tripSummaryMeta = [
		`${num(groupSize)} ${num(groupSize) === 1 ? "guest" : "guests"}`,
		`${num(nights)} ${num(nights) === 1 ? "night" : "nights"}`,
		`${num(rounds)} ${num(rounds) === 1 ? "round" : "rounds"}`,
		String(flexWeeks) === "0" ? "No date flexibility" : FLEX_OPTIONS[flexWeeks] || "No date flexibility",
	]

	// --- Costing (right column) -------------------------------------------
	// Per line: reuse the /quoting per-person engine (calculatePackageCost) for
	// the chosen package + room/board/fee, then multiply by group size for the
	// line total in the package's own currency. Flights/transfers carry their own
	// currency + per-person/total mode. A manual exchange rate converts any
	// non-GBP line to GBP.
	const groupSizeNum = Number(groupSize) || 0
	const costLines = useMemo(() => {
		// Course titles by uid, for the per-course golf-transfer lines.
		const roundTitleByUid = Object.fromEntries(basket.filter((entry) => entry.type === "round").map((entry) => [entry.uid, entry.title]))
		return basket.flatMap((item) => {
				// Flights & transfers carry a chosen currency (default GBP) and a
				// per-person / total price mode. We derive both the line total and the
				// per-person figure from whichever was entered.
				if (item.type === "flight") {
					const price = Number(item.price) || 0
					const isTotal = item.priceMode === "total"
					return {
						uid: item.uid,
						name: item.legs?.[0]?.carrier || "Flights",
						kind: "flight",
						currency: item.currency || "gbp",
						priced: price > 0,
						perPerson: isTotal ? (groupSizeNum > 0 ? price / groupSizeNum : 0) : price,
						lineTotal: isTotal ? price : price * groupSizeNum,
					}
				}
				// A Euro Tunnel crossing is priced per booking, so its net price is a
				// group total; the per-person figure is derived from it.
				if (item.type === "eurotunnel") {
					const price = Number(item.price) || 0
					return {
						uid: item.uid,
						name: "Euro Tunnel",
						kind: "eurotunnel",
						currency: item.currency || "gbp",
						priced: price > 0,
						perPerson: groupSizeNum > 0 ? price / groupSizeNum : 0,
						lineTotal: price,
					}
				}
				if (item.type === "transfer") {
					// A golf transfer priced per course (the bulk-add flow) emits one
					// line per ticked course, each with its own price + price mode.
					// Legacy single-course golf transfers (item-level price) fall
					// through to the single-line path below.
					if (item.transferType === "golf" && Array.isArray(item.courseUids) && item.courseUids.length > 0) {
						return item.courseUids.map((courseUid) => {
							const pricing = (item.coursePrices || {})[courseUid] || {}
							const price = Number(pricing.price) || 0
							const isTotal = pricing.priceMode === "total"
							const courseTitle = roundTitleByUid[courseUid]
							return {
								uid: `${item.uid}-${courseUid}`,
								name: courseTitle ? `Golf transfer · ${courseTitle}` : "Golf transfer",
								kind: "transfer",
								currency: item.currency || "gbp",
								priced: price > 0,
								perPerson: isTotal ? (groupSizeNum > 0 ? price / groupSizeNum : 0) : price,
								lineTotal: isTotal ? price : price * groupSizeNum,
							}
						})
					}
					const price = Number(item.price) || 0
					const isTotal = item.priceMode !== "per_person"
					const name =
						item.transferType === "golf"
							? "Golf transfer"
							: item.transferType === "airport_golf"
							? "Airport & golf transfer"
							: item.transferType === "resort_shuttle"
							? "Resort Shuttles"
							: "Airport transfer"
					return {
						uid: item.uid,
						name,
						kind: "transfer",
						currency: item.currency || "gbp",
						priced: price > 0,
						perPerson: isTotal ? (groupSizeNum > 0 ? price / groupSizeNum : 0) : price,
						lineTotal: isTotal ? price : price * groupSizeNum,
					}
				}

				const pkg = item.selectedPackage
				if (item.type === "hotel") {
					// No package yet -> a zero line (shown as 0, not summed).
					if (!pkg) return { uid: item.uid, name: item.title, kind: "hotel", currency: item.currency, priced: false, perPerson: 0, lineTotal: 0 }
					// Room mix: rates are per person, so each room costs rate x sleeps x
					// quantity; summed across the picked room types. Board stays per person
					// on the group. Per-night packages multiply the lot by the chosen nights.
					const nightsMult = pkg.type === "per_night" ? Number(item.nights) || 0 : 1
					const roomRate = (key) => Number((pkg.room_types || []).find((room) => room.type === key)?.cost) || 0
					const roomsTotal =
						item.selectedRooms.reduce((sum, room) => {
							const sleeps = parseInt(room.sleeps) || defaultSleeps(room.key)
							const qty = parseInt(room.qty) || 0
							return sum + roomRate(room.key) * sleeps * qty
						}, 0) * nightsMult
					const boardRate = Number((pkg.board_types || []).find((board) => board.type === item.selectedBoard)?.cost) || 0
					const boardTotal = boardRate * groupSizeNum * nightsMult
					const grossLineTotal = roomsTotal + boardTotal
					const grossPerPerson = groupSizeNum > 0 ? grossLineTotal / groupSizeNum : 0
					// Apply any chosen discounts to the per-person figure (the room-mix
					// total can't route through the costing engine, so we mirror its maths).
					const perPerson = applyPerPersonDiscounts(grossPerPerson, item.discountsChosen, groupSizeNum)
					const lineTotal = perPerson * groupSizeNum
					return { uid: item.uid, name: item.title, kind: "hotel", priced: grossLineTotal > 0, currency: item.currency, perPerson, lineTotal, predicted: Boolean(pkg.predicted), predictedFromMillis: pkg.predictedFromMillis || 0, upliftPct: pkg.upliftPct || 0 }
				}
				// A round emits its green-fee line plus, when buggies are "charged",
				// a separate buggy cost line (its own currency) - hence flatMap.
				const buggyLine = buggyCostLine(item, groupSizeNum)
				// A round with no package is treated as included in the trip - a zero line.
				if (!pkg) {
					const zero = { uid: item.uid, name: item.title, kind: "round", currency: item.currency, priced: false, perPerson: 0, lineTotal: 0 }
					return buggyLine ? [zero, buggyLine] : zero
				}
				// The costing engine applies the chosen discounts directly for rounds.
				const perPerson = calculatePackageCost(pkg, { chosenFeeType: { option: item.chosenFeeType }, groupSize: groupSizeNum, discountsChosen: item.discountsChosen || [] })
				const roundLine = { uid: item.uid, name: item.title, kind: "round", priced: true, currency: item.currency, perPerson, lineTotal: perPerson * groupSizeNum, predicted: Boolean(pkg.predicted), predictedFromMillis: pkg.predictedFromMillis || 0, upliftPct: pkg.upliftPct || 0 }
				return buggyLine ? [roundLine, buggyLine] : roundLine
			})
	}, [basket, groupSizeNum])

	const pricedLines = costLines.filter((line) => line.priced)
	// First priced line whose rate is a prior-year estimate (no live rates were
	// loaded for its date); drives the costs-column footnote + its uplift wording.
	const predictedLine = pricedLines.find((line) => line.predicted)
	// Currencies (other than GBP) that need a manual rate to convert.
	const neededRateCurrencies = Array.from(new Set(pricedLines.map((line) => (line.currency || "gbp").toLowerCase()))).filter((cur) => cur !== "gbp")
	const totalGBP = pricedLines.reduce((sum, line) => {
		const cur = (line.currency || "gbp").toLowerCase()
		if (cur === "gbp") return sum + line.lineTotal
		const r = parseFloat(exchangeRates[cur]) || 0
		return r > 0 ? sum + line.lineTotal / r : sum
	}, 0)
	const perPersonGBP = groupSizeNum > 0 ? totalGBP / groupSizeNum : 0

	// Pre-fill the single sell price from the cost-per-person and keep it tracking
	// the cost (e.g. as the exchange rate changes) until the agent edits it. Skips
	// split pricing (a deliberate manual carve-up) and a zero cost.
	useEffect(() => {
		if (sellTouched || splitSell) return
		if (perPersonGBP > 0) setSellAt(String(Math.round(perPersonGBP)))
	}, [perPersonGBP, sellTouched, splitSell])

	// Sell-at is a PER PERSON price. With split pricing on, the sell side is a set
	// of per-person tiers (price x head count) and the single value is ignored;
	// either way a single total-sell figure drives one blended margin.
	const sellAtNum = parseFloat(sellAt) || 0
	const sellLinesTotal = sellLines.reduce((sum, line) => sum + (parseFloat(line.price) || 0) * (parseInt(line.qty) || 0), 0)
	const sellLinesQty = sellLines.reduce((sum, line) => sum + (parseInt(line.qty) || 0), 0)
	const totalSell = splitSell ? sellLinesTotal : sellAtNum * groupSizeNum
	// Blended per-person sell, denormalised onto the draft so the Quotes-panel
	// cards keep showing a single headline figure.
	const sellPerPersonGBP = groupSizeNum > 0 ? totalSell / groupSizeNum : 0

	// Sale figures: total profit and 5% agent commission on the profit, plus the
	// margin % (profit over the total sell price).
	const saleProfitTotal = totalSell - totalGBP
	const margin = totalSell > 0 ? (saleProfitTotal / totalSell) * 100 : 0
	// Margin health: <8% red, 8-15% orange, >18% green, 15-18% neutral.
	const marginClass = margin < 8 ? "is-red" : margin < 15 ? "is-orange" : margin > 18 ? "is-green" : ""
	const commission = saleProfitTotal * 0.05

	// Switch the single sell price into per-person tiers and back. Entering split
	// mode seeds line 1 from the single price across the whole group (line 2 left
	// blank), so the agent only carves off the single-occupancy tier.
	const enableSplitSell = () => {
		setSellLines((current) => {
			if (current.some((line) => line.price || line.qty)) return current
			return [
				{ price: sellAt || "", qty: groupSizeNum ? String(groupSizeNum) : "" },
				{ price: "", qty: "" },
			]
		})
		setSplitSell(true)
		// Engaging split pricing counts as taking over the sell side, so the
		// single-price pre-fill stops tracking cost.
		setSellTouched(true)
	}
	const disableSplitSell = () => {
		if (!sellAt && sellLines[0]?.price) setSellAt(sellLines[0].price)
		setSplitSell(false)
		setSellTouched(true)
	}

	// Tee times are constrained to the trip window (check-in -> return) and the
	// picker opens on the travel date rather than today (the trip is usually in
	// the future, so opening on today would show an all-disabled month).
	const nightsNum = Number(nights) || 0
	// Tee dates are constrained to the trip's travel window (earliest check-in to
	// latest checkout across all stays, else travel date -> travel date + nights).
	const teeWindow = roundTravelWindow(basket, travelMillis, nightsNum)
	const teeOpenOn = teeWindow ? new Date(teeWindow.start) : travelMillis ? new Date(travelMillis) : undefined
	const teeMinDate = teeWindow ? new Date(teeWindow.start) : undefined
	const teeMaxDate = teeWindow ? new Date(teeWindow.end) : undefined

	// Resolve the item whose package modal is open + the date its packages are
	// matched against (a hotel's check-in, a round's earliest tee-time, else the
	// trip travel date).
	const packageModalItem = packageModalUid ? basket.find((item) => item.uid === packageModalUid) : null
	const editItem = editItemUid ? basket.find((item) => item.uid === editItemUid) : null
	const editBasketItem = editBasketUid ? basket.find((item) => item.uid === editBasketUid) : null
	let packageMatchMillis = 0
	if (packageModalItem) {
		if (packageModalItem.type === "hotel") {
			packageMatchMillis = packageModalItem.checkInMillis || travelMillis || 0
		} else {
			const teeMillis = (packageModalItem.teeTimes || []).filter((tee) => tee > 0).sort((a, b) => a - b)[0]
			packageMatchMillis = packageModalItem.teeDate || teeMillis || travelMillis || 0
		}
	}

	// Persist the full builder state as a draft under enquiries/{id}/quotes. The
	// first save creates the doc (stamping `created`); subsequent saves update
	// that same doc, so an agent can keep editing without spawning duplicates.
	// True when we opened an already-sent quote (is_active). Saving it should
	// keep it an active/sent quote, not flip it back to a draft.
	const editingActive = Boolean(initialDraft?.is_active)

	// Build + persist the quote payload, creating a new doc or merging into the
	// existing one. Returns the saved { id, data } so callers (plain save, or
	// save-and-send) can act on the fresh doc. Throws on failure.
	const persistQuote = async () => {
		const payload = {
			is_draft: !editingActive,
			...(editingActive ? { is_active: true } : {}),
			enquiry: resolvedEnquiryID,
			agent: auth.currentUser?.uid || null,
			updated: firebase.firestore.FieldValue.serverTimestamp(),
			// Builder state needed to rehydrate the modal on re-open.
			group_size: groupSizeNum,
			travel_millis: travelMillis || 0,
			flex_weeks: Number(flexWeeks) || 0,
			nights: Number(nights) || 0,
			rounds: Number(rounds) || 0,
			// Strip undefined values (Firestore rejects them) while preserving the
			// price-window Timestamps the modal needs to rehydrate correctly.
			basket: sanitiseForFirestore((basket || []).map(stripWindowsForSave)),
			exchange_rates: sanitiseForFirestore(exchangeRates || {}),
			// `sell_at` stays a single per-person figure (blended when split) for
			// the cards; the tier breakdown rehydrates the builder.
			sell_at: splitSell ? Math.round(sellPerPersonGBP) || null : sellAtNum || null,
			sell_split: splitSell,
			sell_lines: splitSell ? sellLines.map((line) => ({ price: parseFloat(line.price) || 0, qty: parseInt(line.qty) || 0 })) : [],
			// Denormalised summary so the Quotes panel can show figures without
			// re-running the costing engine.
			totals: {
				total_gbp: Math.round(totalGBP),
				per_person_gbp: Math.round(perPersonGBP),
				margin: Number(margin.toFixed(2)),
				sale_profit_total: Number(saleProfitTotal.toFixed(2)),
				commission: Number(commission.toFixed(2)),
			},
		}

		const quotes = db.collection(`enquiries/${resolvedEnquiryID}/quotes`)
		let id = draftId
		if (draftId) {
			await quotes.doc(draftId).set(payload, { merge: true })
		} else {
			payload.created = firebase.firestore.FieldValue.serverTimestamp()
			const ref = await quotes.add(payload)
			id = ref.id
			setDraftId(id)
		}
		return { id, data: payload }
	}

	const handleSaveDraft = async () => {
		if (!resolvedEnquiryID || saving) return
		setSaving(true)
		setSaveError("")
		try {
			await persistQuote()
			setSavedAt(Date.now())
			if (onSaved) onSaved()
		} catch (err) {
			console.error("Could not save quote:", err)
			setSaveError("Could not save - please try again.")
		} finally {
			setSaving(false)
		}
	}

	// Save, then hand the freshly-saved quote to the parent's send flow (which
	// closes this modal and opens the send picker, as if the card's Send icon
	// had been clicked).
	const handleSaveAndSend = async () => {
		if (!resolvedEnquiryID || saving || !onSaveAndSend) return
		setSaving(true)
		setSaveError("")
		try {
			const saved = await persistQuote()
			if (onSaved) onSaved()
			// Preserve an existing reference (sent quotes) so a re-send keeps it, and
			// carry any previously-saved client message forward so the send flow's
			// message step pre-fills with it (the builder no longer edits it).
			onSaveAndSend({ id: saved.id, ...saved.data, reference: initialDraft?.reference, agent_message: initialDraft?.agent_message })
		} catch (err) {
			console.error("Could not save quote:", err)
			setSaveError("Could not save - please try again.")
			setSaving(false)
		}
	}

	// A quote-like projection of the LIVE builder state for the client preview.
	// Mirrors persistQuote's payload shape so the preview matches a saved/sent
	// quote exactly; nothing here is written - it just feeds buildPublicQuoteDoc.
	// Send-time fields (reference, message, expiry, ...) aren't set in the builder,
	// so we carry forward any an opened draft already has.
	const previewQuote = useMemo(
		() => ({
			id: draftId || "preview",
			enquiry: resolvedEnquiryID,
			basket,
			group_size: groupSizeNum,
			travel_millis: travelMillis || 0,
			nights: Number(nights) || 0,
			rounds: Number(rounds) || 0,
			sell_at: splitSell ? Math.round(sellPerPersonGBP) || null : sellAtNum || null,
			sell_split: splitSell,
			sell_lines: splitSell ? sellLines.map((line) => ({ price: parseFloat(line.price) || 0, qty: parseInt(line.qty) || 0 })) : [],
			exchange_rates: exchangeRates,
			agent: auth.currentUser?.uid || initialDraft?.agent || null,
			reference: initialDraft?.reference || "",
			agent_message: initialDraft?.agent_message || "",
			public_token: initialDraft?.public_token || "",
			full_balance_weeks: initialDraft?.full_balance_weeks,
			book_disabled: initialDraft?.book_disabled,
			hidden_discounts: initialDraft?.hidden_discounts,
		}),
		[basket, groupSizeNum, travelMillis, nights, rounds, splitSell, sellPerPersonGBP, sellAtNum, sellLines, exchangeRates, draftId, resolvedEnquiryID, initialDraft],
	)

	const modalContent = (
		<div className="new-quote-overlay">
			<div className={["new-quote-modal", readOnly ? "is-readonly" : ""].join(" ").trim()} role="dialog" aria-modal="true" aria-label={readOnly ? "View quote" : "Create new quote"}>
				{/* Header */}
				<div className="new-quote-header">
					<div className="new-quote-header-title">
						<h2>{readOnly ? "View quote" : "Create new Quote"}</h2>
						{readOnly && <Badge label="Read only" type="INFO" />}
					</div>
					<div className="new-quote-header-actions">
						{resolvedEnquiryID && (
							<button type="button" className="new-quote-header-btn" onClick={() => setEnquiryViewOpen(true)}>
								<FileText size={15} />
								View enquiry
							</button>
						)}
						<button type="button" className="new-quote-header-btn" onClick={() => setPreviewOpen(true)} disabled={basket.length === 0} title={basket.length === 0 ? "Add items to preview the quote" : "See what your client sees"}>
							<Eye size={15} />
							Preview
						</button>
						<button type="button" className="new-quote-close" onClick={onClose} aria-label="Close">
							<X size={20} />
						</button>
					</div>
				</div>

				{/* Body: basket (left, with the seeded params on top) + itinerary preview (right) */}
				<div className="new-quote-body">
					<aside className="new-quote-basket">
						{/* Enquiry-seeded parameters — collapse to a one-line summary once set */}
						<div className="new-quote-params">
							{paramsOpen ? (
								<>
									<div className="new-quote-params-row is-two">
										<Datepicker label="Travel date" showLabel placeholder="Travel date" value={travelMillis} onSelect={handleTravelDateChange} />
										<Select label="Date flexibility" showLabel placeholder="None" value={FLEX_OPTIONS[flexWeeks]} selected={String(flexWeeks)} options={FLEX_OPTIONS} onSelect={({ option }) => setFlexWeeks(option)} />
									</div>
									<div className="new-quote-params-row is-three">
										<Input type="text" label="Group size" showLabel placeholder="Group size" value={groupSize} onChange={setGroupSize} />
										<Input type="text" label="Nights" showLabel placeholder="Nights" value={nights} onChange={setNights} />
										<Input type="text" label="Rounds" showLabel placeholder="Rounds" value={rounds} onChange={setRounds} />
									</div>
									<div className="new-quote-params-done-row">
										<Button small label="Done" onClick={() => setParamsOpen(false)} />
									</div>
								</>
							) : (
								<div className="new-quote-params-summary" onClick={() => setParamsOpen(true)}>
									<div className="new-quote-params-summary-detail">
										<p className="new-quote-params-summary-head">{travelMillis ? moment(travelMillis).format("D MMM YYYY") : "Travel date TBC"}</p>
										<p className="new-quote-params-summary-meta">{tripSummaryMeta.join(" · ")}</p>
									</div>
									<button type="button" className="new-quote-params-edit" onClick={() => setParamsOpen(true)} aria-label="Edit trip details">
										<Pencil size={14} />
									</button>
								</div>
							)}
						</div>

						<div className="new-quote-divider" />

						{/* Search + quick-add flights & transfers, grouped between the dividers */}
						<div className="new-quote-find">
							<InstantSearch searchClient={searchClient} indexName="cms_holidays">
								<div className="new-quote-search">
									<CustomSearchBox />
									<div className="new-quote-results">
										<Index indexName="cms_holidays">
											<ResortResults />
										</Index>
										<Index indexName="cms_courses">
											<CourseResults />
										</Index>
									</div>
								</div>
							</InstantSearch>

							<div className="new-quote-extras">
								<AddTransportMenu onAddFlight={addFlight} onAddTransfer={addTransfer} onAddEuroTunnel={addEuroTunnel} />
							</div>
						</div>

						<div className="new-quote-divider" />

						{adding && (
							<div className="new-quote-adding">
								<Loading />
							</div>
						)}

						{teeResetNotice > 0 && (
							<div className="new-quote-tee-reset-notice">
								<AlertTriangle size={13} />
								<span>
									{teeResetNotice} {teeResetNotice === 1 ? "tee date was" : "tee dates were"} cleared - they fell outside the new travel dates.
								</span>
							</div>
						)}

						<div className="new-quote-basket-list">
							{basket.length === 0 && <p className="new-quote-basket-empty">Search above to add hotels and golf rounds to this quote.</p>}

							{(() => {
								const renderBasketItem = (item) => {
									if (item.type === "hotel")
										return <HotelSummaryCard key={item.uid} item={item} onEdit={() => setEditBasketUid(item.uid)} onRemove={removeItem} />
									if (item.type === "round") return <RoundSummaryCard key={item.uid} item={item} onEdit={() => setEditBasketUid(item.uid)} onCopy={copyRound} onRemove={removeItem} />
									if (item.type === "flight")
										return <FlightSummary key={item.uid} item={item} onEdit={() => setEditItemUid(item.uid)} onRemove={removeItem} />
									if (item.type === "eurotunnel")
										return <EuroTunnelSummary key={item.uid} item={item} onEdit={() => setEditItemUid(item.uid)} onRemove={removeItem} />
									return <TransferSummary key={item.uid} item={item} onEdit={() => setEditItemUid(item.uid)} onRemove={removeItem} hotelTitleByUid={hotelTitleByUid} courseTitleByUid={courseTitleByUid} />
								}
								return (
									<>
										{basketExtras.map(renderBasketItem)}
										{basketExtras.length > 0 && basketMains.length > 0 && <div className="new-quote-basket-divider" />}
										{basketMains.map(renderBasketItem)}
									</>
								)
							})()}
						</div>
					</aside>

					<section className="new-quote-itinerary">
						{nodes.length === 0 ? (
							<div className="new-quote-itinerary-empty">Your itinerary will appear here as you add hotels and rounds.</div>
						) : (
							<ItineraryTimeline nodes={nodes} />
						)}
					</section>

					<aside className="new-quote-costs">
						<div className="new-quote-costs-head">
							<h3>Costs</h3>
						</div>

						<div className="new-quote-costs-list">
							{costLines.length === 0 ? (
								<p className="new-quote-costs-empty">Costs appear here once you add items and pick their packages.</p>
							) : (
								costLines.map((line) => (
									<div className="new-quote-cost-row" key={line.uid}>
										<div className="new-quote-cost-row-detail">
											<span className="new-quote-cost-kind">{line.kind === "hotel" ? "Hotel" : line.kind === "round" ? "Golf" : line.kind === "flight" ? "Flight" : line.kind === "buggies" ? "Buggies" : line.kind === "eurotunnel" ? "Euro Tunnel" : "Transfer"}</span>
											<p>{line.name}</p>
											{line.predicted && (
												<span className="new-quote-cost-estimate">
													<AlertTriangle size={11} /> Est. from {moment(line.predictedFromMillis).format("MMM YYYY")} · +{line.upliftPct}%
												</span>
											)}
										</div>
										<div className="new-quote-cost-figures">
											{line.lineTotal > 0 ? (
												<>
													<strong>
														{printCurrencySymbol(line.currency)}
														{Math.round(line.lineTotal).toLocaleString()}
													</strong>
													<small>
														{printCurrencySymbol(line.currency)}
														{Math.round(line.perPerson).toLocaleString()}pp
													</small>
												</>
											) : (
												<span className="new-quote-cost-unpriced">No price</span>
											)}
										</div>
									</div>
								))
							)}
						</div>

						{predictedLine && (
							<div className="new-quote-costs-estimate-note">
								<AlertTriangle size={13} />
								<p>
									Estimated lines have no live rates loaded for their date - they use the same dates a year earlier with a {predictedLine.upliftPct}% uplift
									applied. Treat them as a guide, not a firm cost.
								</p>
							</div>
						)}

						<div className="new-quote-costs-totals">
							{neededRateCurrencies.length > 0 && (
								<div className="new-quote-costs-rates">
									{neededRateCurrencies.map((cur) => (
										<div className="new-quote-costs-rate" key={cur}>
											<span className="new-quote-rate-label">Exchange rate: GBP → {cur.toUpperCase()}</span>
											<Input type="number" placeholder="e.g. 1.15" value={exchangeRates[cur] || ""} onChange={(value) => setRate(cur, value)} />
										</div>
									))}
								</div>
							)}

							<div className="new-quote-costs-divider" />

							<div className="new-quote-costs-sell">
								{!splitSell ? (
									<>
										<span className="new-quote-rate-label">Sell at price (per person)</span>
										<div className="new-quote-sell-single">
											<Input type="number" symbol="£" placeholder="Per person price" value={sellAt} onChange={handleSellChange} />
											<button
												type="button"
												className="new-quote-sell-split-btn"
												title="Split into multiple price lines"
												aria-label="Split into multiple price lines"
												onClick={enableSplitSell}>
												<ArrowLeftRight size={15} />
											</button>
										</div>
									</>
								) : (
									<>
										<div className="new-quote-sell-split-head">
											<span className="new-quote-rate-label">Sell at price</span>
											<button
												type="button"
												className="new-quote-sell-split-btn is-active"
												title="Back to a single price"
												aria-label="Back to a single price"
												onClick={disableSplitSell}>
												<ArrowLeftRight size={14} />
											</button>
										</div>

										<div className="new-quote-sell-lines">
											{sellLines.map((line, index) => (
												<div className="new-quote-sell-line" key={index}>
													<div className="new-quote-sell-line-price">
														<span className="new-quote-sell-line-label">{index === 0 ? "Sell at price (pp)" : "Single person price (pp)"}</span>
														<Input type="number" symbol="£" placeholder="Per person price" value={line.price} onChange={(value) => setSellLine(index, "price", value)} />
													</div>
													<div className="new-quote-sell-line-qty">
														<span className="new-quote-sell-line-label">Qty</span>
														<Input type="number" placeholder="0" value={line.qty} onChange={(value) => setSellLine(index, "qty", value)} />
													</div>
												</div>
											))}
										</div>

										{groupSizeNum > 0 && sellLinesQty !== groupSizeNum && (
											<div className="new-quote-sell-qty-warning">
												<AlertTriangle size={12} />
												<span>
													Quantities total {sellLinesQty} of {groupSizeNum} {groupSizeNum === 1 ? "traveller" : "travellers"}.
												</span>
											</div>
										)}
									</>
								)}
							</div>

							<div className="new-quote-costs-divider" />

							<div className="new-quote-costs-total-row is-secondary">
								<span>Total cost price</span>
								<strong>£{Math.round(totalGBP).toLocaleString()}</strong>
							</div>
							<div className="new-quote-costs-total-row is-secondary">
								<span>Cost price per person</span>
								<strong>£{Math.round(perPersonGBP).toLocaleString()}</strong>
							</div>

							{/* Margin / profit / commission are shown at all times (they read
							    0 until the sell price is raised above cost). */}
							<div className="new-quote-costs-total-row is-secondary">
								<span>Booking margin</span>
								<strong className={marginClass}>{margin.toFixed(2)}%</strong>
							</div>
							<div className="new-quote-costs-total-row is-secondary">
								<span>Sale profit</span>
								<strong>£{saleProfitTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
							</div>
							<div className="new-quote-costs-total-row is-secondary">
								<span>Commission</span>
								<strong>£{commission.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
							</div>

							<div className="new-quote-costs-actions">
								<Button
									small
									type="secondary"
									label={editingActive ? "Update quote" : draftId ? "Update draft" : "Save as draft"}
									loading={saving}
									loadingText="Saving..."
									onClick={handleSaveDraft}
									disabled={basket.length === 0 || saving || !resolvedEnquiryID}
								/>
								{onSaveAndSend && (
									<Button
										small
										label={editingActive || draftId ? "Update & Send" : "Save & Send"}
										icon={<Send size={15} />}
										onClick={handleSaveAndSend}
										disabled={basket.length === 0 || saving || !resolvedEnquiryID}
									/>
								)}
							</div>

							{/* Save feedback - on its own line below the buttons, so it's never
							    squeezed in beside them. */}
							{saveError || savedAt ? (
								<p className={["new-quote-save-message", saveError ? "is-error" : "is-note"].join(" ")}>
									{saveError || (editingActive ? "Quote saved" : "Draft saved")}
								</p>
							) : null}
						</div>
					</aside>
				</div>
			</div>

			{packageModalItem && (
				<PackageModal
					item={packageModalItem}
					matchMillis={packageMatchMillis}
					flexWeeks={Number(flexWeeks) || 0}
					currencySymbol={printCurrencySymbol(packageModalItem.currency)}
					onClose={() => setPackageModalUid(null)}
					onApplyDate={(millis) => {
						// Accepting the cheaper check-in updates the trip travel date
						// and this hotel's check-in, so the modal re-pulls packages.
						setTravelMillis(millis)
						setBasket((current) => current.map((basketItem) => (basketItem.uid === packageModalItem.uid ? { ...basketItem, checkInMillis: millis } : basketItem)))
					}}
					onSelect={(payload) => {
						selectPackage(packageModalItem.uid, payload)
						setPackageModalUid(null)
						// A hotel package with included rounds populates the resort's
						// connected courses as round items (T7): auto-filled for a single
						// course, or via the allocation picker when there are several.
						if (packageModalItem.type === "hotel") openHotelRounds(packageModalItem, payload.window)
					}}
				/>
			)}

			{coursePicker &&
				(() => {
					const pickerHotel = basket.find((item) => item.uid === coursePicker.hotelUid)
					if (!pickerHotel) return null
					return (
						<ConnectedCoursesModal
							rounds={coursePicker.rounds}
							refs={coursePicker.refs}
							onCancel={() => {
								// Skipping the allocation falls back to the legacy auto-spread.
								addHotelRounds(pickerHotel, cycledRefs(coursePicker.refs, coursePicker.rounds))
								setCoursePicker(null)
							}}
							onConfirm={(orderedRefs) => {
								addHotelRounds(pickerHotel, orderedRefs)
								setCoursePicker(null)
							}}
						/>
					)
				})()}

			{editItem && (
				<FlightTransferModal
					item={editItem}
					hotels={basketHotels}
					courses={basketCourses}
					defaultLegMillis={flightLegDefaultMillis}
					onUpdate={updateItem}
					onSetTripType={setFlightTripType}
					onSetLeg={setFlightLeg}
					onClose={() => setEditItemUid(null)}
				/>
			)}

			{editBasketItem && editBasketItem.type === "hotel" && (
				<HotelEditModal
					item={editBasketItem}
					groupSize={groupSizeNum}
					onClose={() => setEditBasketUid(null)}
					onUpdate={updateItem}
					onCheckInChange={handleHotelCheckInChange}
					onToggleRoom={toggleRoom}
					onSetRoomQty={setRoomQty}
					onSetRoomSleeps={setRoomSleeps}
					onSetBoard={setBoard}
					onToggleDiscount={toggleDiscount}
					onOpenPackages={() => setPackageModalUid(editBasketItem.uid)}
				/>
			)}

			{editBasketItem && editBasketItem.type === "round" && (
				<RoundEditModal
					item={editBasketItem}
					groupSize={groupSizeNum}
					onClose={() => setEditBasketUid(null)}
					onUpdate={updateItem}
					onSetTeeDate={setTeeDate}
					onAddTeeTime={addTeeTime}
					onSetTeeTime={setTeeTime}
					onRemoveTeeTime={removeTeeTime}
					onToggleConnectorTeeTime={toggleConnectorTeeTime}
					onToggleDiscount={toggleDiscount}
					openOn={teeOpenOn}
					minDate={teeMinDate}
					maxDate={teeMaxDate}
					onOpenPackages={() => setPackageModalUid(editBasketItem.uid)}
				/>
			)}

			{enquiryViewOpen && <EnquiryViewModal enquiryID={resolvedEnquiryID} onClose={() => setEnquiryViewOpen(false)} />}

			{previewOpen && <QuotePreviewModal quote={previewQuote} enquiryID={resolvedEnquiryID} onClose={() => setPreviewOpen(false)} />}
		</div>
	)

	// Read-only view (e.g. a cancelled quote): wrap in the read-only context so
	// every write Button hides itself; the editor inputs are locked via CSS.
	return readOnly ? <ReadOnlyProvider value={true}>{modalContent}</ReadOnlyProvider> : modalContent
}

/**
 * Read-only notes + applicable discounts held against a hotel/course, filtered
 * to the item's own date window. Notes are display-only; ticking a discount
 * feeds it into the cost line (cost/margin only - the sell price is unaffected).
 */
function ItemNotesDiscounts({ item, fromMillis, toMillis, onToggleDiscount }) {
	const notes = (item.notes || []).filter((note) => overlapsWindow(note, fromMillis, toMillis))
	const discounts = (item.discounts || []).filter((discount) => overlapsWindow(discount, fromMillis, toMillis))
	if (notes.length === 0 && discounts.length === 0) return null
	const chosenIds = new Set((item.discountsChosen || []).map((entry) => entry.id))

	return (
		<div className="new-quote-notes-discounts">
			{notes.length > 0 && (
				<div className="new-quote-resort-notes">
					<span className="new-quote-rooms-label">Notes</span>
					{notes.map((note, index) => (
						<div className="new-quote-resort-note" key={note.id || index}>
							{note.start_date?.seconds && note.end_date?.seconds ? (
								<small>
									{moment(note.start_date.seconds, "X").format("DD MMM YYYY")} - {moment(note.end_date.seconds, "X").format("DD MMM YYYY")}
								</small>
							) : null}
							<p>{note.text}</p>
						</div>
					))}
				</div>
			)}

			{discounts.length > 0 && (
				<div className="new-quote-resort-discounts">
					<span className="new-quote-rooms-label">Discounts</span>
					{discounts.map((discount) => (
						<div className="new-quote-resort-discount" key={discount.id}>
							<Checkbox
								label={`${discount.title || "Discount"}${discountSummary(discount) ? ` - ${discountSummary(discount)}` : ""}`}
								checked={chosenIds.has(discount.id)}
								onClick={() => onToggleDiscount(item.uid, discount)}
							/>
							{(discount.start_date?.seconds || discount.end_date?.seconds) && (
								<span className="new-quote-resort-discount-dates">
									Valid {discount.start_date?.seconds ? moment(discount.start_date.seconds, "X").format("DD MMM YYYY") : "?"} - {discount.end_date?.seconds ? moment(discount.end_date.seconds, "X").format("DD MMM YYYY") : "?"}
								</span>
							)}
						</div>
					))}
				</div>
			)}
		</div>
	)
}

// Brief one-line summary of a configured hotel, shown on its collapsed card and
// echoed in the edit modal footer (so the running config stays visible).
const hotelSummaryLine = (item) => {
	const parts = []
	const nights = Number(item.nights) || 0
	if (nights > 0) parts.push(`${nights} ${nights === 1 ? "night" : "nights"}${item.checkInMillis ? ` from ${moment(item.checkInMillis).format("D MMM")}` : ""}`)
	else if (item.checkInMillis) parts.push(moment(item.checkInMillis).format("D MMM"))
	if (item.selectedBoard) parts.push(BOARD_TYPES[item.selectedBoard] || item.selectedBoard)
	;(item.selectedRooms || []).forEach((room) => {
		const qty = parseInt(room.qty) || 0
		parts.push(`${qty > 0 ? `${qty}x ` : ""}${ROOM_TYPES[room.key] || room.key}`)
	})
	const rounds = Number(item.selectedPackage?.rounds) || 0
	if (rounds > 0) parts.push(`${rounds} ${rounds === 1 ? "round" : "rounds"}`)
	if (parts.length === 0) return item.selectedPackage ? "Package selected" : "Tap to choose dates & package"
	return parts.join(" · ")
}

// Brief one-line summary of a configured round.
const roundSummaryLine = (item) => {
	const parts = []
	const times = (item.teeTimes || []).filter((tee) => tee > 0)
	if (item.teeDate) parts.push(moment(item.teeDate).format("D MMM"))
	if (times.length > 0) parts.push(`${times.length} tee ${times.length === 1 ? "time" : "times"}`)
	else if (item.teeDate) parts.push("times to confirm")
	if (item.chosenFeeType) parts.push(FEE_TYPES[item.chosenFeeType] || item.chosenFeeType)
	if (normaliseBuggies(item.buggies).enabled) parts.push("buggies")
	if (parts.length === 0) return item.selectedPackage ? "Green fees selected" : "Tap to choose a date & green fees"
	return parts.join(" · ")
}

/**
 * Compact basket card for a configured hotel/round: thumbnail + name + a one-line
 * summary of what's chosen, with edit (opens the full modal) and remove actions.
 * Clicking anywhere on the card opens the editor.
 */
function ItemSummaryCard({ className, badgeLabel, badgeType, image, title, summary, onEdit, onCopy, copyLabel, onRemove, removeLabel }) {
	return (
		<div className={["new-quote-item-card", className].join(" ").trim()} onClick={onEdit}>
			<PreviewImage src={image} className="new-quote-item-thumb" iconSize={18} />
			<div className="new-quote-item-card-detail">
				<div className="new-quote-item-card-head">
					<Badge label={badgeLabel} type={badgeType} />
					<p className="new-quote-item-card-title">{title}</p>
				</div>
				<p className="new-quote-item-card-summary">{summary}</p>
			</div>
			<div className="new-quote-item-card-actions">
				{/* No edit button - the whole card is the edit target. The copy +
				    remove buttons reveal on hover (see .new-quote-item-card SCSS). */}
				{onCopy && (
					<button
						type="button"
						className="new-quote-item-copy"
						onClick={(event) => {
							event.stopPropagation()
							onCopy()
						}}
						aria-label={copyLabel}
						title={copyLabel}>
						<Copy size={15} />
					</button>
				)}
				<button
					type="button"
					className="new-quote-item-remove"
					onClick={(event) => {
						event.stopPropagation()
						onRemove()
					}}
					aria-label={removeLabel}>
					<Trash2 size={15} />
				</button>
			</div>
		</div>
	)
}

function HotelSummaryCard({ item, onEdit, onRemove }) {
	return <ItemSummaryCard className="is-hotel" badgeLabel="Hotel" badgeType="INFO" image={item.image} title={item.title} summary={hotelSummaryLine(item)} onEdit={onEdit} onRemove={() => onRemove(item.uid)} removeLabel="Remove hotel" />
}

function RoundSummaryCard({ item, onEdit, onCopy, onRemove }) {
	return <ItemSummaryCard className="is-round" badgeLabel="Golf" badgeType="POSITIVE" image={item.image} title={item.title} summary={roundSummaryLine(item)} onEdit={onEdit} onCopy={() => onCopy(item.uid)} copyLabel="Duplicate round a day later" onRemove={() => onRemove(item.uid)} removeLabel="Remove round" />
}

/**
 * Allocation picker for a hotel package's included rounds across the resort's
 * connected courses. Mirrors the room-types picker (checkbox rows + a quantity
 * stepper, no "sleeps"): the agent spreads the N included rounds over the courses
 * - one course ×N, N courses ×1, or any mix - and Confirm only unlocks once the
 * quantities total exactly N. Cancelling falls back to the legacy auto-spread.
 */
function ConnectedCoursesModal({ rounds, refs, onCancel, onConfirm }) {
	// Quantity per course id (0 / absent = not chosen).
	const [qtys, setQtys] = useState({})
	const setQty = (id, qty) => setQtys((current) => ({ ...current, [id]: qty }))
	// Row click toggles the course on (qty 1) / off (qty 0), like the room rows.
	const toggle = (id) => setQtys((current) => ({ ...current, [id]: current[id] ? 0 : 1 }))

	const allocated = refs.reduce((sum, ref) => sum + (Number(qtys[ref.id]) || 0), 0)
	const matches = allocated === rounds

	const confirm = () => {
		if (!matches) return
		// Expand to one ref per round (each course repeated by its quantity).
		const ordered = []
		refs.forEach((ref) => {
			const qty = Number(qtys[ref.id]) || 0
			for (let i = 0; i < qty; i++) ordered.push(ref)
		})
		onConfirm(ordered)
	}

	return (
		<div className="package-modal-overlay is-form-modal">
			<div className="package-modal flight-modal" role="dialog" aria-modal="true" aria-label="Choose included courses">
				<div className="package-modal-header">
					<div>
						<h3>Included rounds</h3>
						<p>
							{rounds} {rounds === 1 ? "round" : "rounds"} included - choose which courses
						</p>
					</div>
					<button type="button" className="package-modal-close" onClick={onCancel} aria-label="Close">
						<X size={18} />
					</button>
				</div>

				<div className="package-modal-body">
					<div className="flight-form">
						<div className="new-quote-courses-section">
							<div className="new-quote-courses-head">
								<span className="new-quote-rooms-label">Connected courses</span>
								<span className={["new-quote-courses-count", matches ? "is-complete" : ""].join(" ").trim()}>
									{allocated} of {rounds} allocated
								</span>
							</div>
							<div className="new-quote-rooms-list">
								{refs.map((ref) => {
									const qty = Number(qtys[ref.id]) || 0
									const on = qty > 0
									return (
										<div key={ref.id} className={["new-quote-room-row", on ? "is-on" : ""].join(" ").trim()} onClick={() => toggle(ref.id)}>
											<div className="ui-checkbox new-quote-room-check">
												<div className={["ui-checkbox-box", on ? "is-checked" : ""].join(" ").trim()}>
													<CheckIcon />
												</div>
												<label>{ref.title}</label>
											</div>
											{on && (
												<div className="new-quote-room-controls" onClick={(event) => event.stopPropagation()}>
													<div className="new-quote-room-ctrl">
														<span className="new-quote-room-ctrl-label">Qty</span>
														<Stepper value={qty} min={1} ariaLabel="quantity" onChange={(value) => setQty(ref.id, value)} />
													</div>
												</div>
											)}
										</div>
									)
								})}
							</div>
						</div>
					</div>
				</div>

				<div className="package-modal-foot">
					<Button type="secondary" small label="Cancel" onClick={onCancel} />
					<Button small label="Confirm" disabled={!matches} onClick={confirm} />
				</div>
			</div>
		</div>
	)
}

/**
 * Full hotel editor, opened from the summary card. A single airy column (the
 * direction picked from the T16 mockups) mirroring the flight/transfer modal:
 * package, dates, board, rooms and notes/discounts, with a live one-line recap
 * in the footer.
 */
function HotelEditModal({ item, groupSize = 0, onClose, onUpdate, onCheckInChange, onToggleRoom, onSetRoomQty, onSetRoomSleeps, onSetBoard, onToggleDiscount, onOpenPackages }) {
	const pkg = item.selectedPackage
	const isPerNight = pkg?.type === "per_night"
	const currencySymbol = printCurrencySymbol(item.currency)

	// Room + board options come from the chosen package, each showing its
	// per-person cost (matching the /quoting page). Rooms alphabetical, boards
	// by price (cheapest first).
	const roomTypes = pkg
		? (pkg.room_types || [])
				.map((room) => ({ key: room.type, name: ROOM_TYPES[room.type] || room.type, label: `${ROOM_TYPES[room.type] || room.type} - ${currencySymbol}${Number(room.cost) || 0}` }))
				.sort((a, b) => a.name.localeCompare(b.name))
		: []
	const boardTypes = pkg
		? (pkg.board_types || []).map((board) => ({ key: board.type, label: BOARD_TYPES[board.type] || board.type, cost: Number(board.cost) || 0 })).sort((a, b) => a.cost - b.cost)
		: []
	const boardOptions = boardTypes.reduce((options, boardType) => {
		options[boardType.key] = `${boardType.label} - ${currencySymbol}${boardType.cost}`
		return options
	}, {})

	// Included-golf course list: the package's explicit included_courses when
	// filled, otherwise the resort's linked courses (the /quoting source).
	const namedIncluded = pkg ? (pkg.included_courses || []).filter((course) => course.name) : []
	const includedCourses = namedIncluded.length > 0 ? namedIncluded : (item.linkedCourses || []).map((name) => ({ name }))

	// Total beds across the chosen rooms (quantity x sleeps). When it exceeds the
	// group size we surface a non-blocking warning so the agent notices an
	// over-booked room mix (e.g. group of 4 but 2 twins + a single = 5 beds).
	const roomCapacity = (item.selectedRooms || []).reduce((sum, room) => sum + (parseInt(room.qty) || 0) * (parseInt(room.sleeps) || defaultSleeps(room.key)), 0)
	const overCapacity = groupSize > 0 && roomCapacity > groupSize

	return (
		<div className="package-modal-overlay is-form-modal" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
			<div className="package-modal flight-modal" role="dialog" aria-modal="true" aria-label="Edit hotel">
				<div className="package-modal-header">
					<div>
						<h3>Hotel details</h3>
						<p>{item.title}</p>
					</div>
					<button type="button" className="package-modal-close" onClick={onClose} aria-label="Close">
						<X size={18} />
					</button>
				</div>

				<div className="package-modal-body">
					<div className="flight-form">
						<div className="new-quote-hotel-top">
				{/* Package preview / CTA sits above the first divider */}
			<div className="new-quote-package-cta">
					<Button label={pkg ? "Change package" : "Select package"} onClick={onOpenPackages} />
				</div>

			<div className="new-quote-item-fields">
				<Datepicker label="Check in" showLabel placeholder="Check in date" value={item.checkInMillis} onSelect={(millis) => onCheckInChange(item.uid, millis)} />
				<Input type="text" label="Nights" showLabel placeholder="Nights" value={item.nights} readOnly={Boolean(pkg) && !isPerNight} onChange={(value) => onUpdate(item.uid, { nights: value })} />
			</div>
			</div>

			{pkg && (
				<div className="new-quote-package-chosen">
					<div className="new-quote-package-chosen-detail">
						<p className="new-quote-package-name">{pkg.name?.replace(/&#039;/g, "'") || "Package"}</p>
						<p className="new-quote-package-meta">
							{isPerNight ? `${item.nights} nights` : `${pkg.nights} nights`}
							{pkg.rounds > 0 ? ` · ${pkg.rounds} rounds` : ""}
							{pkg.predicted ? ` · est. +${pkg.upliftPct}%` : ""}
						</p>
					</div>
					<button type="button" className="new-quote-package-change" onClick={onOpenPackages} aria-label="Change package">
						<Pencil size={14} />
					</button>
				</div>
			)}

			{pkg && (
				<div className="new-quote-package-section">
					{boardTypes.length > 0 && (
						<div className="new-quote-board">
							<span className="new-quote-rooms-label">Board type</span>
							<Select selected={item.selectedBoard} options={boardOptions} placeholder="Choose board type" onSelect={({ option }) => onSetBoard(item.uid, option)} />
						</div>
					)}

					<div className="new-quote-rooms-section">
						<span className="new-quote-rooms-label">Room types</span>
						{roomTypes.length === 0 ? (
							<small className="new-quote-rooms-empty">This package has no room types.</small>
						) : (
							<div className="new-quote-rooms-list">
								{roomTypes.map((roomType) => {
									const selectedRoom = item.selectedRooms.find((room) => room.key === roomType.key)
									return (
										<div
											key={roomType.key}
											className={["new-quote-room-row", selectedRoom ? "is-on" : ""].join(" ").trim()}
											onClick={() => onToggleRoom(item.uid, roomType.key)}>
											{/* Visual checkbox (the whole row is the toggle) + name. */}
											<div className="ui-checkbox new-quote-room-check">
												<div className={["ui-checkbox-box", selectedRoom ? "is-checked" : ""].join(" ").trim()}>
													<CheckIcon />
												</div>
												<label>{roomType.label}</label>
											</div>
											{selectedRoom && (
												<div className="new-quote-room-controls" onClick={(event) => event.stopPropagation()}>
													<div className="new-quote-room-ctrl">
														<span className="new-quote-room-ctrl-label">Qty</span>
														<Stepper value={selectedRoom.qty} min={1} ariaLabel="quantity" onChange={(value) => onSetRoomQty(item.uid, roomType.key, value)} />
													</div>
													<div className="new-quote-room-ctrl">
														<span className="new-quote-room-ctrl-label">Sleeps</span>
														<Stepper value={selectedRoom.sleeps ?? defaultSleeps(roomType.key)} min={1} ariaLabel="sleeps" onChange={(value) => onSetRoomSleeps(item.uid, roomType.key, value)} />
													</div>
												</div>
											)}
										</div>
									)
								})}
							</div>
						)}
					{overCapacity && (
						<div className="new-quote-rooms-warning">
							<AlertTriangle size={13} />
							<span>The selected rooms sleep {roomCapacity}, which is more than the group size of {groupSize}.</span>
						</div>
					)}
					</div>
					</div>
			)}

			{pkg && Number(pkg.rounds) > 0 && (
				<div className="new-quote-included-golf">
					<span className="new-quote-rooms-label">Connected courses</span>
					<p className="new-quote-included-golf-summary">
						{Number(pkg.rounds) || 0} {Number(pkg.rounds) === 1 ? "round" : "rounds"} included
					</p>
					{includedCourses.length > 0 && (
						<ul className="new-quote-included-golf-list">
							{includedCourses.map((course, index) => (
								<li key={index}>
									{course.name}
									{course.quantity ? ` ×${course.quantity}` : ""}
								</li>
							))}
						</ul>
					)}
				</div>
			)}
			<ItemNotesDiscounts
				item={item}
				fromMillis={item.checkInMillis || 0}
				toMillis={item.checkInMillis ? moment(item.checkInMillis).add(Number(item.nights) || 0, "days").valueOf() : 0}
				onToggleDiscount={onToggleDiscount}
			/>
					</div>
				</div>

				<div className="package-modal-foot">
					<Button small label="Done" onClick={onClose} />
				</div>
			</div>
		</div>
	)
}

// A tee time takes at most four golfers, so a bigger group goes out in flights
// of up to four across multiple times (8 → 4+4, 6 → 4+2).
const MAX_FLIGHT_SIZE = 4

// Chosen times within this many minutes of each other count as back-to-back;
// anything further apart means the group is visibly separated on the day.
const BACK_TO_BACK_GAP_MINUTES = 12

/**
 * Full round editor, opened from the summary card: buggies, tee date + times,
 * green-fee package and notes/discounts in one airy column.
 */
function RoundEditModal({ item, groupSize = 0, onClose, onUpdate, onSetTeeDate, onAddTeeTime, onSetTeeTime, onRemoveTeeTime, onToggleConnectorTeeTime, onToggleDiscount, openOn, minDate, maxDate, onOpenPackages }) {
	const pkg = item.selectedPackage
	const teeTimes = item.teeTimes || []
	const buggies = normaliseBuggies(item.buggies)
	const setBuggies = (patch) => onUpdate(item.uid, { buggies: { ...buggies, ...patch } })

	// Live availability via the course's connector (if any). We look up the active
	// connector once on open; absent one, the whole fetch affordance stays hidden.
	const [connector, setConnector] = useState(null)
	const [liveSlots, setLiveSlots] = useState(null)
	const [fetching, setFetching] = useState(false)
	const [fetchError, setFetchError] = useState("")
	// The date the currently-shown slots were pulled for, so changing the round's
	// date visibly invalidates a stale list rather than silently mismatching.
	const [slotsForMillis, setSlotsForMillis] = useState(0)

	useEffect(() => {
		if (!item.cmsId) return undefined
		let cancelled = false
		db.collection(`CMS_courses/${item.cmsId}/connectors`)
			.where("status", "==", "active")
			.limit(1)
			.get()
			.then((snap) => {
				if (cancelled || snap.empty) return
				setConnector({ id: snap.docs[0].id })
			})
			.catch(() => {
				// Non-fatal — no connector affordance shows if the lookup fails.
			})
		return () => {
			cancelled = true
		}
	}, [item.cmsId])

	// A live list is only valid for the date it was pulled for; drop it when the
	// round's date moves so the agent re-fetches against the new day.
	useEffect(() => {
		if (slotsForMillis && item.teeDate !== slotsForMillis) {
			setLiveSlots(null)
			setFetchError("")
		}
	}, [item.teeDate, slotsForMillis])

	const fetchLiveTeeTimes = async () => {
		if (!connector || !item.teeDate || fetching) return
		setFetching(true)
		setFetchError("")
		setLiveSlots(null)
		try {
			const date = moment(item.teeDate).format("YYYY-MM-DD")
			const { data } = await fetchConnectorTeeTimes({ entityKind: "CMS_COURSE", entityId: item.cmsId, connectorId: connector.id, date })
			setLiveSlots(Array.isArray(data?.teeTimes) ? data.teeTimes : [])
			setSlotsForMillis(item.teeDate)
		} catch (err) {
			setFetchError(err?.message || "Couldn't fetch live tee times — try again.")
		} finally {
			setFetching(false)
		}
	}

	// Is this live slot ("HH:MM") already one of the round's chosen tee times?
	const slotIsAdded = (slotTime) => teeTimes.some((tee) => tee && moment(tee).format("HH:mm") === slotTime)

	// The smallest flight the group would send out: the whole group up to four,
	// then the remainder once full fours are peeled off (8 → 4, 6 → 2, 5 → 1).
	// A slot only needs to fit that smallest flight to be worth picking.
	const smallestFlight = groupSize <= MAX_FLIGHT_SIZE ? groupSize : groupSize % MAX_FLIGHT_SIZE || MAX_FLIGHT_SIZE

	// When the round holds several tee times, flag any chosen time sitting more
	// than the back-to-back gap from every other chosen time — the group is
	// split across separated slots and the agent should see it at a glance.
	const chosenSorted = teeTimes.filter((tee) => tee > 0).sort((a, b) => a - b)
	const separatedTimes = new Set(
		chosenSorted
			.filter((millis, index) => {
				if (chosenSorted.length < 2) return false
				const gapBefore = index > 0 ? millis - chosenSorted[index - 1] : Infinity
				const gapAfter = index < chosenSorted.length - 1 ? chosenSorted[index + 1] - millis : Infinity
				return Math.min(gapBefore, gapAfter) > BACK_TO_BACK_GAP_MINUTES * 60 * 1000
			})
			.map((millis) => moment(millis).format("HH:mm")),
	)
	const gapTitle = `More than ${BACK_TO_BACK_GAP_MINUTES} minutes from the group's other tee times`

	// While a pulled sheet is on screen, its grid IS the picker: selecting a slot
	// there is enough, so the manual list above hides any time already shown in the
	// grid (no duplicate rows) and "Add tee time" is reserved for off-sheet times.
	// Selections still live in `teeTimes`, so the itinerary/save are unaffected.
	const hasLiveSlots = Array.isArray(liveSlots) && liveSlots.length > 0
	const pulledTimeSet = new Set((liveSlots || []).map((slot) => slot.time))
	const manualTeeEntries = teeTimes
		.map((millis, index) => ({ millis, index }))
		.filter(({ millis }) => !(hasLiveSlots && millis && pulledTimeSet.has(moment(millis).format("HH:mm"))))

	return (
		<div className="package-modal-overlay is-form-modal" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
			<div className="package-modal flight-modal" role="dialog" aria-modal="true" aria-label="Edit round">
				<div className="package-modal-header">
					<div>
						<h3>Golf round</h3>
						<p>{item.title}</p>
					</div>
					<button type="button" className="package-modal-close" onClick={onClose} aria-label="Close">
						<X size={18} />
					</button>
				</div>

				<div className="package-modal-body">
					<div className="flight-form">
			<div className="new-quote-round-top">
							<div className="new-quote-round-fields">
								<Datepicker label="Date" showLabel placeholder="Date" value={item.teeDate || 0} openOn={openOn} minDate={minDate} maxDate={maxDate} onSelect={(value) => onSetTeeDate(item.uid, value)} />
							</div>
							<div className="new-quote-package-cta">
								<Button label={pkg ? "Change package" : "Select package"} onClick={onOpenPackages} />
							</div>
						</div>

						{pkg && (
							<div className="new-quote-package-chosen">
								<div className="new-quote-package-chosen-detail">
									<p className="new-quote-package-name">{FEE_TYPES[item.chosenFeeType] || pkg.name?.replace(/&#039;/g, "'") || "Green fees"}</p>
									<p className="new-quote-package-meta">Green fee{pkg.predicted ? ` · est. +${pkg.upliftPct}%` : ""}</p>
								</div>
								<button type="button" className="new-quote-package-change" onClick={onOpenPackages} aria-label="Change package">
									<Pencil size={14} />
								</button>
							</div>
						)}

						<div className="new-quote-tee-times">
							<span className="new-quote-rooms-label">Times</span>
							{!item.teeDate ? (
								<small className="new-quote-rooms-empty">Choose a date first to add tee times.</small>
							) : (
								<>
									<div className="new-quote-tee-list">
										{manualTeeEntries.length === 0
											? !hasLiveSlots && <small className="new-quote-rooms-empty">No times yet - the date will show as "times to be confirmed".</small>
											: manualTeeEntries.map(({ millis, index }) => (
													<div className="new-quote-tee-row" key={index}>
														<Timepicker placeholder="Tee time" wrapperClass="is-datepicker" icon={<Clock size={14} />} value={millis || null} onSelect={(value) => onSetTeeTime(item.uid, index, value)} />
														<button type="button" className="new-quote-tee-remove" onClick={() => onRemoveTeeTime(item.uid, index)} aria-label="Remove tee time">
															<X size={14} />
														</button>
														{millis > 0 && separatedTimes.has(moment(millis).format("HH:mm")) && (
															<span className="new-quote-tee-gap" title={gapTitle}>
																<AlertTriangle size={13} />
																Tee time not sequential
															</span>
														)}
													</div>
											  ))}
									</div>

									<div className="new-quote-add-tee-row">
										{connector && (
											<Button
												small
												label="Pull tee sheet"
												icon={<Plug size={14} />}
												loading={fetching}
												loadingText="Pulling…"
												disabled={fetching}
												onClick={fetchLiveTeeTimes}
											/>
										)}
										<Button type="secondary" small label="Add tee time" icon={<Plus size={14} />} disabled={hasLiveSlots} onClick={() => onAddTeeTime(item.uid)} />
									</div>

									{connector && (liveSlots !== null || fetchError) && (
										<div className="new-quote-live-panel">
											{fetchError ? (
												<p className="new-quote-live-error">{fetchError}</p>
											) : liveSlots.length === 0 ? (
												<p className="new-quote-live-empty">No live tee times for {moment(item.teeDate).format("D MMM")}.</p>
											) : (
												<>
													<p className="new-quote-live-head">
														Live availability · {moment(item.teeDate).format("D MMM")}
														<span className="new-quote-live-count">{liveSlots.length}</span>
													</p>
													<div className="new-quote-live-list">
														{liveSlots.map((slot) => {
															const added = slotIsAdded(slot.time)
															const spaces = Number(slot.slots)
															const knownSpaces = Number.isFinite(spaces) && spaces > 0
															// Too few spaces for even the group's smallest flight — can't book
															// it, so block adding it (an already-added slot stays removable).
															// Bigger groups split into fours, so a slot with four spaces is
															// always pickable however large the group is.
															const tooFew = groupSize > 0 && knownSpaces && spaces < smallestFlight
															const disabled = tooFew && !added
															const separated = added && separatedTimes.has(slot.time)
															return (
																<button
																	type="button"
																	key={slot.time}
																	className={["new-quote-live-slot", added ? "is-added" : "", disabled ? "is-disabled" : ""].join(" ").trim()}
																	disabled={disabled}
																	title={disabled ? `Only ${slot.slots} of the ${smallestFlight} spaces needed` : undefined}
																	onClick={() => !disabled && onToggleConnectorTeeTime(item.uid, slot.time)}>
																	<span className="new-quote-live-time">{slot.time}</span>
																	{knownSpaces && (
																		<span className="new-quote-live-spaces">
																			{slot.slots} {slot.slots === 1 ? "space" : "spaces"}
																		</span>
																	)}
																	<span className="new-quote-live-action">
																		{disabled ? (
																			"Too few"
																		) : (
																			<>
																				{added ? <Check size={14} /> : <Plus size={14} />}
																				{added ? "Added" : "Add"}
																			</>
																		)}
																	</span>
																	{separated && (
																		<span className="new-quote-live-gap" title={gapTitle}>
																			<AlertTriangle size={12} />
																			Tee time not sequential
																		</span>
																	)}
																</button>
															)
														})}
													</div>
												</>
											)}
										</div>
									)}
								</>
							)}
						</div>

						<div className="new-quote-buggies">
							<span className="new-quote-rooms-label has-gap">Buggies</span>
							<Toggle label="Include buggies" on={buggies.enabled} onClick={() => setBuggies({ enabled: !buggies.enabled })} />

							{buggies.enabled && (
								<div className="new-quote-buggy-detail">
									<Segmented className="new-quote-buggy-status" options={BUGGY_STATUS_OPTIONS} selected={buggies.status} onSelect={({ option }) => setBuggies({ status: option })} />

									{buggies.status === "charged" && (
										<div className="new-quote-buggy-pricing">
											<Select label="Currency" showLabel value={CURRENCY_OPTIONS[buggies.currency]} selected={buggies.currency} options={CURRENCY_OPTIONS} onSelect={({ option }) => setBuggies({ currency: option })} />
											<Input
												wrapperClass="new-quote-buggy-qty"
												type="number"
												label="Quantity"
												showLabel
												placeholder="Quantity"
												value={buggies.quantity}
												onChange={(value) => setBuggies({ quantity: value })}
												note={groupSize > 0 ? `Group of ${groupSize} ${groupSize === 1 ? "golfer" : "golfers"}` : "Number of buggies"}
											/>
											<Input wrapperClass="new-quote-buggy-price" type="number" label="Net cost" showLabel symbol={printCurrencySymbol(buggies.currency)} placeholder="Net cost" value={buggies.price} onChange={(value) => setBuggies({ price: value })} note="Per buggy" />
										</div>
									)}
								</div>
							)}
						</div>

						<ItemNotesDiscounts item={item} fromMillis={item.teeDate || 0} toMillis={item.teeDate || 0} onToggleDiscount={onToggleDiscount} />
					</div>
				</div>

				<div className="package-modal-foot">
					<Button small label="Done" onClick={onClose} />
				</div>
			</div>
		</div>
	)
}

/**
 * Single "Add transport" affordance replacing the separate flight/transfer
 * buttons: a full-width trigger with a small dropdown beneath it offering
 * flight / transfer / Euro Tunnel. Closes on an outside click or after a pick.
 */
function AddTransportMenu({ onAddFlight, onAddTransfer, onAddEuroTunnel }) {
	const [open, setOpen] = useState(false)
	const wrapRef = useRef(null)

	useEffect(() => {
		if (!open) return undefined
		const onDown = (event) => {
			if (wrapRef.current && !wrapRef.current.contains(event.target)) setOpen(false)
		}
		document.addEventListener("mousedown", onDown)
		return () => document.removeEventListener("mousedown", onDown)
	}, [open])

	const pick = (action) => {
		setOpen(false)
		action()
	}

	return (
		<div className="new-quote-add-transport" ref={wrapRef}>
			<button type="button" className={["new-quote-add-transport-btn", open ? "is-open" : ""].join(" ").trim()} onClick={() => setOpen((value) => !value)} aria-haspopup="menu" aria-expanded={open}>
				<Car size={15} />
				<span>Add transport</span>
				<ChevronDown size={14} className="new-quote-add-transport-caret" />
			</button>

			{open && (
				<div className="new-quote-add-transport-menu" role="menu">
					<button type="button" role="menuitem" className="new-quote-add-transport-option" onClick={() => pick(onAddFlight)}>
						<Plane size={15} />
						Add flight
					</button>
					<button type="button" role="menuitem" className="new-quote-add-transport-option" onClick={() => pick(onAddTransfer)}>
						<Car size={15} />
						Add transfer
					</button>
					<button type="button" role="menuitem" className="new-quote-add-transport-option" onClick={() => pick(onAddEuroTunnel)}>
						<TrainFront size={15} />
						Add Euro Tunnel
					</button>
				</div>
			)}
		</div>
	)
}

/**
 * A flight basket row: carrier (name + IATA code), one-way / return, and the
 * per-leg details (airports, departure + arrival datetimes, stops) plus luggage,
 * golf bags and the price to us. All manual entry - nothing is booked.
 */
function FlightSummary({ item, onEdit, onRemove }) {
	const leg = item.legs[0] || {}
	const arrow = item.tripType === "return" ? "⇄" : "→"
	const route = leg.fromAirport || leg.toAirport ? `${leg.fromAirport || "?"} ${arrow} ${leg.toAirport || "?"}` : "Route to set"
	const priceLabel = Number(item.price) > 0 ? ` · ${printCurrencySymbol(item.currency || "gbp")}${Number(item.price).toLocaleString()}${item.priceMode === "total" ? "" : "pp"}` : ""

	return (
		<ExtraSummary badgeLabel="Flight" name={leg.carrier || "Flight"} meta={`${route}${priceLabel}`} onEdit={onEdit} onRemove={() => onRemove(item.uid)} removeLabel="Remove flight" />
	)
}

/**
 * Compact transfer summary in the basket; edit opens the modal.
 */
/**
 * Compact Euro Tunnel summary in the basket: trip type, crossing date(s) and
 * the net price (a group total) in its chosen currency; edit opens the modal.
 */
function EuroTunnelSummary({ item, onEdit, onRemove }) {
	const isReturn = (item.tripType || "return") === "return"
	const dates = [
		item.outboundMillis ? moment(item.outboundMillis).format("D MMM") : null,
		isReturn && item.returnMillis ? moment(item.returnMillis).format("D MMM") : null,
	]
		.filter(Boolean)
		.join(" ⇄ ")
	const priceLabel = Number(item.price) > 0 ? `${printCurrencySymbol(item.currency || "gbp")}${Number(item.price).toLocaleString()}` : "Price to set"
	const meta = [isReturn ? "Return" : "One way", dates, priceLabel].filter(Boolean).join(" · ")
	return <ExtraSummary badgeLabel="Transport" name="Euro Tunnel" meta={meta} onEdit={onEdit} onRemove={() => onRemove(item.uid)} removeLabel="Remove Euro Tunnel" />
}

function TransferSummary({ item, onEdit, onRemove, hotelTitleByUid, courseTitleByUid }) {
	const TRANSFER_TYPES = { airport: "Airport", golf: "Golf", airport_golf: "Airport & golf", resort_shuttle: "Resort Shuttles" }
	const hotelName = hotelTitleByUid[item.hotelUid] || "hotel"
	const courseName = courseTitleByUid[item.courseUid] || "course"
	const airportName = item.airportLabel || item.airport || "airport"
	const currencySymbol = printCurrencySymbol(item.currency || "gbp")

	// A golf transfer can serve several ticked courses (each priced inline);
	// legacy ones carry a single courseUid + item-level price.
	const golfUids = item.transferType === "golf" && Array.isArray(item.courseUids) && item.courseUids.length ? item.courseUids : null

	let route
	if (golfUids) {
		const names = golfUids.map((uid) => courseTitleByUid[uid]).filter(Boolean)
		route = `${hotelName} ⇄ ${names.length ? names.join(", ") : "courses"}`
	} else if (item.transferType === "golf") route = `${hotelName} ⇄ ${courseName}`
	else if (item.transferType === "airport_golf") route = `${airportName} ⇄ ${hotelName} ⇄ ${courseName}`
	else if (item.transferType === "resort_shuttle") {
		const names = (item.courseUids || []).map((uid) => courseTitleByUid[uid]).filter(Boolean)
		const courseList = names.length ? names.join(", ") : "courses"
		route = `${hotelName} ⇄ ${courseList}`
	} else route = `${airportName} ⇄ ${hotelName}`

	// Price hint. Multi-course golf: a single summed figure when every priced
	// course shares a mode (suffixed pp when per person); mixed modes just count
	// the priced courses - the costs column carries the detail either way.
	let priceLabel = ""
	if (golfUids) {
		const priced = golfUids.map((uid) => (item.coursePrices || {})[uid]).filter((pricing) => Number(pricing?.price) > 0)
		const modes = new Set(priced.map((pricing) => pricing.priceMode || "per_person"))
		const total = priced.reduce((sum, pricing) => sum + Number(pricing.price), 0)
		if (priced.length > 0 && modes.size === 1) {
			priceLabel = ` · ${currencySymbol}${total.toLocaleString()}${modes.has("per_person") ? "pp" : ""}`
		} else if (priced.length > 0) {
			priceLabel = ` · ${priced.length} priced ${priced.length === 1 ? "course" : "courses"}`
		}
	} else if (Number(item.price) > 0) {
		priceLabel = ` · ${currencySymbol}${Number(item.price).toLocaleString()}${item.priceMode === "per_person" ? "pp" : ""}`
	}

	return (
		<ExtraSummary badgeLabel="Transfer" name={TRANSFER_TYPES[item.transferType]} meta={`${route}${priceLabel}`} onEdit={onEdit} onRemove={() => onRemove(item.uid)} removeLabel="Remove transfer" />
	)
}

/**
 * Shared compact card for a flight/transfer basket entry: badge + name + a
 * one-line summary. The whole card is the edit target (matching the hotel/round
 * cards); the remove (trash) action reveals on hover.
 */
function ExtraSummary({ badgeLabel, name, meta, onEdit, onRemove, removeLabel }) {
	return (
		<div className="new-quote-extra-item" onClick={onEdit}>
			<div className="new-quote-extra-detail">
				<div className="new-quote-extra-head">
					<Badge label={badgeLabel} type="DEFAULT" />
					<p className="new-quote-extra-name">{name}</p>
				</div>
				<p className="new-quote-extra-meta">{meta}</p>
			</div>
			<div className="new-quote-extra-actions">
				<button
					type="button"
					className="new-quote-item-remove"
					onClick={(event) => {
						event.stopPropagation()
						onRemove()
					}}
					aria-label={removeLabel}>
					<Trash2 size={15} />
				</button>
			</div>
		</div>
	)
}
